import { isObject } from "wombat-global/src/utilities/object";
import { isObject as isObject$1, traverseObjectPath } from "wombat-global/src/utilities";
import { asArray } from "wombat-global/src/utilities/array";
import { isEmpty } from "wombat-global/src/utilities/empty";
//#region src/utilities/anvil-events.ts
/** Checks one event against the prior ID and persisted sequence bounds. */
const isValidEventSequenceEntry = (event, previousId, snapshot) => Number.isSafeInteger(event.id) && event.id > previousId && event.id < snapshot.n_id && Number.isSafeInteger(event.tid) && event.tid >= 1 && event.tid < snapshot.n_tid && (event.ceid === void 0 || Number.isSafeInteger(event.ceid) && event.ceid >= 1 && event.ceid < event.id);
/** Validates persisted sequence counters and the ordered events they describe. */
const isValidEventSequence = (snapshot) => {
	if (!Number.isSafeInteger(snapshot.n_id) || snapshot.n_id < 1 || !Number.isSafeInteger(snapshot.n_tid) || snapshot.n_tid < 1 || !Number.isSafeInteger(snapshot.discardedThrough) || snapshot.discardedThrough < 0 || snapshot.discardedThrough >= snapshot.n_id) return false;
	let previousId = snapshot.discardedThrough;
	for (const event of snapshot.events) {
		if (!isValidEventSequenceEntry(event, previousId, snapshot)) return false;
		previousId = event.id;
	}
	return true;
};
//#endregion
//#region src/utilities/deep-equal.ts
/**
* Uses the native own-property check without invoking userland object methods.
*/
const hasOwnProperty$1 = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const maxDeepEqualDepth = 100;
/**
* Keeps array comparison order-sensitive so layer snapshots only stay stable
* when repeated fields have the same values in the same positions.
*/
const arraysEqual = (left, right, depth) => {
	if (left.length !== right.length) return false;
	return left.every((leftValue, index) => deepEqualInternal(leftValue, right[index], depth + 1));
};
/**
* Performs the recursive comparison used by form layer synchronization. The
* depth guard prevents pathological data from blocking render/effect work.
* 
* @todo We need a variant of this that ignores ordering or array entries (so out of order arrays are considered equivalent)
* 
*/
const deepEqualInternal = (left, right, depth) => {
	if (depth > maxDeepEqualDepth) return false;
	if (Object.is(left, right)) return true;
	if (Array.isArray(left) || Array.isArray(right)) {
		if (!Array.isArray(left) || !Array.isArray(right)) return false;
		return arraysEqual(left, right, depth);
	}
	if (!isObject(left) || !isObject(right)) return false;
	if (left instanceof Date || right instanceof Date) {
		if (!(left instanceof Date) || !(right instanceof Date)) return false;
		return Object.is(left.getTime(), right.getTime());
	}
	if (Object.getPrototypeOf(left) !== Object.getPrototypeOf(right)) return false;
	if (Object.keys(left).length !== Object.keys(right).length) return false;
	return Object.keys(left).every((key) => {
		if (!hasOwnProperty$1(right, key)) return false;
		return deepEqualInternal(Object.getOwnPropertyDescriptor(left, key)?.value, Object.getOwnPropertyDescriptor(right, key)?.value, depth + 1);
	});
};
/**
* Exposes a deterministic deep equality check for configuration/value snapshots
* that need content comparison rather than referential equality.
*/
const deepEqual = (left, right) => deepEqualInternal(left, right, 0);
//#endregion
//#region src/core/anvil-events.ts
const encodeAnvilEventValue = (value) => {
	if (value === void 0) return { type: "undefined" };
	if (typeof value === "number" && (!Number.isFinite(value) || Object.is(value, -0))) return {
		type: "number",
		value: Object.is(value, -0) ? "-0" : String(value)
	};
	if (value === null || typeof value === "string" || typeof value === "boolean" || typeof value === "number") return value;
	if (Array.isArray(value)) return {
		type: "array",
		items: value.map(encodeAnvilEventValue)
	};
	if (typeof value === "object") return {
		type: "object",
		entries: Object.entries(value).map(([key, child]) => [key, encodeAnvilEventValue(child)])
	};
	throw new TypeError("Anvil history accepts data, not executable values.");
};
const decodeAnvilEventValue = (value) => {
	if (value === null || typeof value !== "object") return value;
	switch (value.type) {
		case "undefined": return;
		case "number": return Number(value.value);
		case "array": return value.items.map(decodeAnvilEventValue);
		case "object": return Object.fromEntries(value.entries.map(([key, child]) => [key, decodeAnvilEventValue(child)]));
	}
};
/** Serializable mutation journal. It never replays changes or alters form values. */
var AnvilEvents = class {
	options;
	/**
	* Committed events retained for reads and snapshots;
	* trimmed only by explicit retention or clear().
	* */
	entries = [];
	/**
	* Monotonic event ID
	* */
	nextEventId = 1;
	/**
	* Allocated for each root run();
	* nested operations share the current transaction ID.
	* */
	nextTransactionId = 1;
	/**
	* Last removed event ID;
	* tells snapshot consumers that the retained history has a missing prefix.
	* */
	discardedThrough = 0;
	/**
	* Holds the current operation's name, source, transaction, and pending changes.
	* record() combines repeated writes to the same kind/layer/path here, keeping
	* the first before-value and latest after-value. flush() adds those changes
	* to the history as one event, then empties the pending changes.
	*/
	frame;
	observers = /* @__PURE__ */ new Set();
	/**
	* Committed events awaiting observer delivery,
	* independent of retained entries and persistence.
	* */
	pending = [];
	/**
	* true during observer delivery so synchronous feedback mutations can be rejected.
	* */
	publishing = false;
	/**
	* Serializes persistence writes;
	* absorbs failures here so later saves can still run.
	* */
	saveQueue = Promise.resolve();
	lastObserverError;
	/**
	* Snapshot owner, taken from options or restored history;
	* an explicit ID must match restored data.
	* */
	entityId;
	/** Options provide initial history and identity, plus retention and persistence settings used afterward. */
	constructor(options = {}) {
		this.options = options;
		const snapshot = options.snapshot;
		if (options.maxEvents !== void 0 && (!Number.isSafeInteger(options.maxEvents) || options.maxEvents < 1)) throw new RangeError("maxEvents must be a positive integer.");
		if (snapshot && (snapshot.version !== 1 || options.entityId !== void 0 && snapshot.eid !== options.entityId)) throw new TypeError("Incompatible Anvil event history.");
		this.entityId = options.entityId ?? snapshot?.eid;
		if (snapshot) {
			if (!isValidEventSequence(snapshot)) throw new TypeError("Invalid Anvil event sequence.");
			this.entries = structuredClone(snapshot.events);
			this.nextEventId = snapshot.n_id;
			this.nextTransactionId = snapshot.n_tid;
			this.discardedThrough = snapshot.discardedThrough;
			this.trim();
		}
	}
	getSnapshot() {
		this.flush();
		return {
			version: 1,
			eid: this.entityId,
			n_id: this.nextEventId,
			n_tid: this.nextTransactionId,
			discardedThrough: this.discardedThrough,
			events: structuredClone(this.entries)
		};
	}
	read(afterEventId = 0) {
		this.flush();
		return structuredClone(this.entries.filter((event) => event.id > afterEventId));
	}
	clear() {
		this.assertWritable();
		this.flush();
		this.discardedThrough = this.nextEventId - 1;
		this.entries = [];
	}
	/** Save explicitly; callers can await failures and retry without losing events. */
	persist() {
		const persistence = this.options.persistence;
		if (!persistence) return Promise.reject(/* @__PURE__ */ new Error("No Anvil event persistence configured."));
		const snapshot = this.getSnapshot();
		const save = this.saveQueue.then(() => persistence.save(snapshot));
		this.saveQueue = save.catch(() => void 0);
		return save;
	}
	subscribe(observer) {
		this.observers.add(observer);
		return { unsubscribe: () => {
			this.observers.delete(observer);
		} };
	}
	/** Capture before scheduling work; resume explicitly after an async boundary. */
	captureContext() {
		this.flush();
		return this.frame ? { ...this.frame.context } : void 0;
	}
	withContext(context, callback) {
		this.assertWritable();
		this.flush();
		const previous = this.frame;
		this.frame = context ? {
			context: { ...context },
			operation: "derived",
			source: "system",
			changes: /* @__PURE__ */ new Map()
		} : void 0;
		try {
			return callback();
		} finally {
			this.flush();
			this.frame = previous;
		}
	}
	/**
	* Context lasts only for the synchronous callback, never across an await.
	*/
	run(operation, source, callback) {
		this.assertWritable();
		this.flush();
		const previous = this.frame;
		const context = previous ? { ...previous.context } : {
			tid: this.nextTransactionId++,
			act: operation,
			r_src: source
		};
		this.frame = {
			context,
			operation,
			source,
			changes: /* @__PURE__ */ new Map()
		};
		try {
			return callback();
		} finally {
			this.flush();
			if (previous) previous.context.ceid = this.frame.context.ceid;
			this.frame = previous;
		}
	}
	record(kind, path, before, after, layer) {
		this.assertWritable();
		if (deepEqual(before, after)) return false;
		if (!this.frame) return this.run("store.update", "system", () => this.record(kind, path, before, after, layer));
		const key = JSON.stringify([
			kind,
			layer,
			path
		]);
		const existing = this.frame.changes.get(key);
		const change = {
			kind,
			path,
			layer,
			before: existing ? existing.before : encodeAnvilEventValue(before),
			after: encodeAnvilEventValue(after)
		};
		if (deepEqual(change.before, change.after)) this.frame.changes.delete(key);
		else this.frame.changes.set(key, change);
		return true;
	}
	/** Commit before store subscribers run so derived writes can cite their cause. */
	flush() {
		const frame = this.frame;
		if (!frame || frame.changes.size === 0) return;
		const event = {
			...frame.context,
			id: this.nextEventId++,
			t: Date.now(),
			op: frame.operation,
			src: frame.source,
			c: [...frame.changes.values()]
		};
		frame.changes.clear();
		frame.context.ceid = event.id;
		this.entries.push(event);
		this.trim();
		if (this.observers.size > 0) {
			this.pending.push(event);
			if (this.pending.length === 1) queueMicrotask(() => this.publish());
		}
	}
	trim() {
		const limit = this.options.maxEvents;
		if (limit !== void 0 && this.entries.length > limit) {
			const removed = this.entries.splice(0, this.entries.length - limit);
			this.discardedThrough = removed[removed.length - 1].id;
		}
	}
	assertWritable() {
		if (this.publishing) throw new Error("Anvil event observers must not mutate the form synchronously.");
	}
	publish() {
		const events = this.pending;
		this.pending = [];
		this.publishing = true;
		try {
			for (const observer of [...this.observers]) try {
				observer.next(structuredClone(events));
			} catch (error) {
				this.lastObserverError = error;
			}
		} finally {
			this.publishing = false;
		}
	}
};
//#endregion
//#region src/core/anvil-form-state-subscription.ts
/**
* Distinguishes observer arguments from options objects so the overloaded
* subscribe API can support both whole-form and path-scoped subscriptions.
*/
const isAnvilObserver = (value) => {
	if (typeof value !== "object" || value === null) return false;
	return typeof Object.getOwnPropertyDescriptor(value, "next")?.value === "function";
};
var AnvilFormStateSubscriptionController = class {
	form;
	constructor(form) {
		this.form = form;
	}
	subscribe(pathOrObserver, observerOrOptions, options = {}) {
		if (typeof pathOrObserver === "string") {
			if (!isAnvilObserver(observerOrOptions)) throw new TypeError("A path state subscription requires an observer.");
			return this._subscribePath(pathOrObserver, observerOrOptions, options);
		}
		if (isAnvilObserver(observerOrOptions)) return this._subscribeForm(pathOrObserver);
		return this._subscribeForm(pathOrObserver, observerOrOptions);
	}
	/**
	* Subscribes to whole-form state changes and emits the form's public state
	* shape rather than raw store maps.
	*/
	_subscribeForm(observer, options = {}) {
		return this.form.store.subscribe({ next: () => {
			observer.next(this.form.getState());
		} }, { debounceMs: options.debounceMs });
	}
	/**
	* Subscribes to a path-scoped field state slice, with optional child rollup,
	* so UI hooks can rerender only when the requested branch changes.
	*/
	_subscribePath(path, observer, options) {
		let timer;
		const includeChildren = options.includeChildren ?? true;
		const emit = () => {
			timer = void 0;
			observer.next(this.form.getState(path, { includeChildren }));
		};
		const subscription = this.form.store.subscribeField(path, { next: () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(emit, options.debounceMs ?? 0);
		} }, { includeChildren });
		return { unsubscribe: () => {
			if (timer) clearTimeout(timer);
			subscription.unsubscribe();
		} };
	}
};
//#endregion
//#region src/core/constants.ts
/**
* Public item identity property included on object-array values returned from
* Anvil reads and submit payloads.
*/
const arrayItemKeyProperty = "__key";
/**
* Internal normalized-array ordering property used inside flat store layers.
*/
const arrayKeysProperty = "__arrayKeys";
/**
* Reserved root used to isolate subform instance data from the parent form
* payload until submit transforms it into subEntries.
*/
const anvilSubFormsRootPath = "__forms";
/**
* Browser storage key used to persist whether Anvil diagnostic logging is
* enabled between page loads.
*/
const anvilDebugStorageKey = "__wombat.anvil.debug";
//#endregion
//#region src/core/keys.ts
const durableKeyAlphabet = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const createdDurableKeys = /* @__PURE__ */ new Set();
const minimumDurableKeyLength = 4;
/**
* Produces a bounded random index with crypto entropy when available so durable
* path ids are hard to collide while still working in non-browser runtimes.
*/
const getRandomIndex = (max) => {
	const randomValues = new Uint32Array(1);
	if (globalThis.crypto) {
		globalThis.crypto.getRandomValues(randomValues);
		const randomValue = randomValues[0];
		if (randomValue !== void 0) return randomValue % max;
	}
	return Math.floor(Math.random() * max);
};
/**
* Builds one candidate durable key from the configured alphabet.
*/
const createRandomDurableKey = (length) => {
	let key = "";
	for (let index = 0; index < length; index += 1) key += durableKeyAlphabet[getRandomIndex(62)] ?? "0";
	return key;
};
/**
* Falls back to deterministic generation after repeated random collisions so
* key creation always terminates unless the address space is exhausted.
*/
const createSequencedDurableKey = (length) => {
	let offset = createdDurableKeys.size;
	while (offset < Number.MAX_SAFE_INTEGER) {
		const key = offset.toString(36).padStart(length, "0").slice(-length);
		if (!createdDurableKeys.has(key)) return key;
		offset += 1;
	}
	throw new Error("Unable to create a unique durable key.");
};
/**
* Creates a process-unique durable key used to decouple store layer paths from
* public form paths, especially when array indices shift.
*/
const createDurableKey = (length = 8) => {
	if (!Number.isInteger(length) || length < minimumDurableKeyLength) throw new Error("Durable key length must be an integer of at least 4.");
	for (let attempt = 0; attempt < 128; attempt += 1) {
		const key = createRandomDurableKey(length);
		if (!createdDurableKeys.has(key)) {
			createdDurableKeys.add(key);
			return key;
		}
	}
	const key = createSequencedDurableKey(length);
	createdDurableKeys.add(key);
	return key;
};
//#endregion
//#region src/utilities/array.ts
/**
* Detects the internal ordering marker used for normalized object arrays.
*/
const isStringArray = (value) => Array.isArray(value) && value.every((entry) => typeof entry === "string");
/**
* Narrows values to plain array item records before normalized array transforms
* read keyed children from them.
*/
const isArrayObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);
//#endregion
//#region src/utilities/path.ts
/** Matches a numeric segment in a dot-separated path, such as `items.0.label`. */
const numericPathSegmentPattern = /(^|\.)\d+(\.|$)/;
/**
* Splits a public Anvil path into non-empty segments for traversal and matching.
*/
const parsePath = (path) => path.split(".");
/**
* Joins optional path parts while skipping missing segments so callers can build
* nested paths without leaking extra separators.
*/
const joinPath = (...parts) => {
	return parts.filter(Boolean).join(".");
};
/**
* Converts durable array item keys in a store path back to public indexes.
*
* @todo I feel like this in combo with the one place its used should be replaced with a
* traverse object that does a check on key segment match failure to see if the arrayKey is
* present
*/
const getPathWithArrayIndexes = (path, getArrayKeys) => {
	const indexedSegments = [];
	const resolvedSegments = [];
	for (const segment of parsePath(path)) {
		const arrayKeys = getArrayKeys(resolvedSegments.join("."));
		if (!isStringArray(arrayKeys)) {
			indexedSegments.push(segment);
			resolvedSegments.push(segment);
			continue;
		}
		const keyIndex = arrayKeys.indexOf(segment);
		const requestedIndex = Number(segment);
		const indexedArrayKey = Number.isInteger(requestedIndex) && requestedIndex >= 0 && String(requestedIndex) === segment ? arrayKeys[requestedIndex] : void 0;
		indexedSegments.push(keyIndex === -1 ? segment : String(keyIndex));
		resolvedSegments.push(indexedArrayKey ?? segment);
	}
	return indexedSegments.join(".");
};
const resolveFormValueArrayIndex = (values, segment) => {
	const numericIndex = Number(segment);
	if (Number.isInteger(numericIndex) && numericIndex >= 0 && numericIndex < values.length && numericIndex.toString() === segment) return numericIndex;
	const durableKeyIndex = values.findIndex((value) => isObject$1(value) && value["__key"] === segment);
	return durableKeyIndex >= 0 ? durableKeyIndex : void 0;
};
/**
* Converts durable array keys in an Anvil field path to the current indexes in
* a public form value. Public group values are arrays whose object entries
* retain their durable key in `__key`.
*/
const resolveFormValuePath = (path, formValue) => {
	if (!path) return path;
	const resolvedSegments = [];
	let currentValue = formValue;
	for (const segment of parsePath(path)) {
		if (Array.isArray(currentValue)) {
			const index = resolveFormValueArrayIndex(currentValue, segment);
			if (index === void 0) return;
			resolvedSegments.push(index.toString());
			currentValue = currentValue[index];
			continue;
		}
		if (!isObject$1(currentValue) || !Object.hasOwn(currentValue, segment)) return;
		resolvedSegments.push(segment);
		currentValue = currentValue[segment];
	}
	return resolvedSegments.join(".");
};
const replaceValueAtSegments = (source, segments, segmentIndex, value) => {
	if (segmentIndex === segments.length) return value;
	const segment = segments[segmentIndex];
	if (Array.isArray(source)) {
		const arrayIndex = Number(segment);
		if (!Number.isInteger(arrayIndex) || arrayIndex < 0 || arrayIndex >= source.length) return source;
		const currentValue = source[arrayIndex];
		const nextValue = replaceValueAtSegments(currentValue, segments, segmentIndex + 1, value);
		if (nextValue === currentValue) return source;
		const nextSource = [...source];
		nextSource[arrayIndex] = nextValue;
		return nextSource;
	}
	if (!isObject$1(source) || !Object.hasOwn(source, segment)) return source;
	const currentValue = source[segment];
	const nextValue = replaceValueAtSegments(currentValue, segments, segmentIndex + 1, value);
	if (nextValue === currentValue) return source;
	return {
		...source,
		[segment]: nextValue
	};
};
/**
* Replaces an existing nested value while preserving references outside the
* changed branch. Invalid paths leave the source object unchanged.
*/
const replaceValueAtPath = (source, path, value) => {
	const nextSource = replaceValueAtSegments(source, parsePath(path), 0, value);
	return isObject$1(nextSource) ? nextSource : source;
};
/**
* Detects parent/child path conflicts so flat store writes avoid ambiguous
* values such as both `profile` and `profile.name`.
*/
const isPathRelated = (left, right) => left !== right && (left.startsWith(`${right}.`) || right.startsWith(`${left}.`));
/**
* Matches exact paths and descendants for subscriptions, deletes, and state
* rollups that need prefix semantics.
*/
const isPathPrefixMatch = (path, prefix) => path === prefix || path.startsWith(`${prefix}.`);
/**
* Extracts descendants under a path and strips the parent prefix so parent reads
* can unflatten only the requested slice.
*/
const getPrefixValues = (values, path) => {
	const result = {};
	const prefix = `${path}.`;
	for (const key of Object.keys(values)) if (key.startsWith(prefix)) result[key.slice(prefix.length)] = values[key];
	return result;
};
/**
* Resolves a public path to its stable durable storage key when one already
* exists, otherwise leaves untouched paths readable.
*/
const getDurablePath = (path, invertedPathMap) => {
	if (invertedPathMap[path]) return invertedPathMap[path];
	return path;
};
/**
* Allocates a durable storage key for a public path. This decouples stored layer
* entries from public array index changes and keeps existing values stable.
*/
const getOrCreateDurablePath = (path, pathMap, invertedPathMap) => {
	const existingPath = getDurablePath(path, invertedPathMap);
	if (existingPath !== path) return existingPath;
	let durablePath = createDurableKey();
	while (pathMap[durablePath] !== void 0) durablePath = createDurableKey();
	pathMap[durablePath] = path;
	invertedPathMap[path] = durablePath;
	return durablePath;
};
//#endregion
//#region src/core/types/values.ts
function isAnvilStoreSentinel(value) {
	if (typeof value !== "object" || value === null) return false;
	const sentinelType = Reflect.get(value, "__wombatAnvilStoreSentinel");
	return (sentinelType === "deleted" || sentinelType === "empty-object") && Reflect.get(value, "version") === 1;
}
const anvilDeletedValue = Object.freeze({
	__wombatAnvilStoreSentinel: "deleted",
	version: 1
});
function isAnvilDeletedValue(value) {
	if (value === anvilDeletedValue) return true;
	if (typeof value !== "object" || value === null) return false;
	return Reflect.get(value, "__wombatAnvilStoreSentinel") === "deleted" && Reflect.get(value, "version") === 1;
}
const anvilEmptyObjectValue = Object.freeze({
	__wombatAnvilStoreSentinel: "empty-object",
	version: 1
});
function isAnvilEmptyObjectValue(value) {
	if (value === anvilEmptyObjectValue) return true;
	if (typeof value !== "object" || value === null) return false;
	return Reflect.get(value, "__wombatAnvilStoreSentinel") === "empty-object" && Reflect.get(value, "version") === 1;
}
//#endregion
//#region src/utilities/unflatten.ts
/**
* Writes one flat path into a nested object before array reconstruction runs.
*/
const assignNestedValueFromSegments = (target, segments, startIndex, value) => {
	let cursor = target;
	for (let index = startIndex; index < segments.length; index += 1) {
		const segment = segments[index];
		if (index === segments.length - 1) {
			cursor[segment] = value;
			continue;
		}
		const next = cursor[segment];
		if (isAnvilEmptyObjectValue(next)) {
			const nextRecord = {};
			cursor[segment] = nextRecord;
			cursor = nextRecord;
			continue;
		}
		if (!isArrayObject(next)) {
			const nextRecord = {};
			cursor[segment] = nextRecord;
			cursor = nextRecord;
			continue;
		}
		cursor = next;
	}
};
/**
* Converts normalized array records back to public arrays. A record containing
* `__arrayKeys` becomes an ordered array. Object items receive their `_key`,
* while primitive arrays read back as primitive arrays.
*/
const transformArrayRecords = (value) => {
	if (isAnvilEmptyObjectValue(value)) return {};
	if (Array.isArray(value)) return value.map((entry) => transformArrayRecords(entry));
	if (!isArrayObject(value)) return value;
	const keys = value[arrayKeysProperty];
	if (isStringArray(keys)) {
		if (!keys.some((key) => {
			return !Object.prototype.hasOwnProperty.call(value, key) || isArrayObject(value[key]);
		})) return keys.map((key) => transformArrayRecords(value[key]));
		return keys.map((key) => {
			if (!Object.prototype.hasOwnProperty.call(value, key)) return { [arrayItemKeyProperty]: key };
			const item = value[key];
			if (isArrayObject(item)) {
				const transformedItem = transformArrayRecords(item);
				if (isArrayObject(transformedItem)) return {
					[arrayItemKeyProperty]: key,
					...transformedItem
				};
				return {
					[arrayItemKeyProperty]: key,
					value: transformedItem
				};
			}
			return {
				[arrayItemKeyProperty]: key,
				value: transformArrayRecords(item)
			};
		});
	}
	const transformed = {};
	for (const [key, entry] of Object.entries(value)) transformed[key] = transformArrayRecords(entry);
	return transformed;
};
/**
* Converts store values using path segments computed by the store. `startIndex`
* allows prefix reads to reuse a full cached path without allocating a slice.
*/
const unflattenStoreValues = (values) => {
	const result = {};
	for (const storeValue of values) assignNestedValueFromSegments(result, storeValue.pathSegments, storeValue.startIndex, storeValue.value);
	return transformArrayRecords(result);
};
//#endregion
//#region src/core/anvil-array-management.ts
/**
* Allocates the shortest available stable array key for one array scope. Keys
* are monotonic `k{number}` values so new items stay compact and predictable.
*/
const createArrayKey = (usedKeys, nextIndex) => {
	let key = `k${nextIndex.value}`;
	while (usedKeys.has(key)) {
		nextIndex.value += 1;
		key = `k${nextIndex.value}`;
	}
	usedKeys.add(key);
	nextIndex.value += 1;
	return key;
};
/**
* Reads a consumer-provided item key from an array object. Existing keys are
* preserved so hydrated arrays and UI-rendered rows keep stable identity.
*/
const getExistingItemKey = (item, itemKeyProperty) => {
	if (!isArrayObject(item)) return;
	const key = item[itemKeyProperty];
	if (typeof key === "string" && key.length > 0) return key;
};
/**
* Counts user-provided keys before assignment so duplicated keys can be ignored
* instead of creating ambiguous array item identity.
*/
const getProvidedItemKeyCounts = (items, itemKeyProperty) => {
	const keyCounts = {};
	for (const item of items) {
		const existingKey = getExistingItemKey(item, itemKeyProperty);
		if (existingKey) keyCounts[existingKey] = (keyCounts[existingKey] ?? 0) + 1;
	}
	return keyCounts;
};
/**
* Moves the generated key cursor past any valid provided `k{number}` keys so
* newly generated keys do not collide with hydrated entries.
*/
const advanceNextIndexForKey = (key, nextIndex) => {
	const keyIndex = Number(key.slice(1));
	if (key.startsWith("k") && Number.isInteger(keyIndex)) nextIndex.value = Math.max(nextIndex.value, keyIndex + 1);
};
/**
* Creates the normalized key list for an array. Unique provided item key values
* win; missing or duplicate keys receive the next shortest `k{number}` value
* that does not collide with existing keys in the same array.
*/
const createArrayKeys = (items, itemKeyProperty) => {
	const providedKeyCounts = getProvidedItemKeyCounts(items, itemKeyProperty);
	const usedKeys = new Set(Object.keys(providedKeyCounts));
	const nextIndex = { value: 0 };
	for (const providedKey of Object.keys(providedKeyCounts)) advanceNextIndexForKey(providedKey, nextIndex);
	return items.map((item) => {
		const existingKey = getExistingItemKey(item, itemKeyProperty);
		if (existingKey && providedKeyCounts[existingKey] === 1) return existingKey;
		return createArrayKey(usedKeys, nextIndex);
	});
};
//#endregion
//#region src/core/generators.ts
/**
* Normalizes optional caller timestamps so layer and value writes can compare
* source freshness using the same unix time.
*/
const getLayerTimestamp = (timestamp) => {
	if (timestamp !== void 0 && Number.isFinite(timestamp)) return timestamp;
	return Date.now();
};
/**
* Creates an empty layer container with a write timestamp for future rollups.
*/
const createLayer = (timestamp) => {
	return {
		_l: {},
		_d: getLayerTimestamp(timestamp)
	};
};
/**
* Wraps a primitive or normalized array key value with the timestamp metadata
* needed to decide which layer wins during rollup.
*/
const createStoreValue = (value, timestamp) => ({
	_v: value,
	_d: getLayerTimestamp(timestamp)
});
/**
* Identifies values that can be stored directly in one flat layer entry.
* Arrays are intentionally excluded because all arrays use keyed path expansion
* before storage.
*/
const isPrimitive = (value) => {
	if (value === null || value === void 0 || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return true;
	return false;
};
const isStoreLeafValue = (value) => isPrimitive(value) || isAnvilStoreSentinel(value);
/**
* Allows explicit primitive arrays only for normalized key lists; ordinary array
* values are expanded into keyed child paths.
*/
const isPrimitiveArray = (value) => value.every(isPrimitive);
/**
* Detects explicit normalized array key paths so generated layers preserve the
* key list as one primitive array instead of expanding it again.
*/
const isArrayKeyListPath = (path) => path === "__arrayKeys" || path.endsWith(`.__arrayKeys`);
/**
* Writes a single flat value into a layer. Store construction uses durable
* paths, while raw layer replacement can opt out to preserve already-normalized
* paths.
*/
const writeLayerValue = (layer, pathMap, invertedPathMap, path, value, timestamp, useDurablePaths) => {
	const layerPath = useDurablePaths ? getOrCreateDurablePath(path, pathMap, invertedPathMap) : path;
	layer._l[layerPath] = createStoreValue(value, timestamp);
};
/**
* Recursively flattens constructor/reset input into layer entries. Object arrays
* become `path.__arrayKeys` plus `path.k0.child` style entries; nested objects
* become dotted paths.
*/
const writeInputValue = (layer, pathMap, invertedPathMap, path, value, timestamp, useDurablePaths) => {
	if (isStoreLeafValue(value)) {
		writeLayerValue(layer, pathMap, invertedPathMap, path, value, timestamp, useDurablePaths);
		return;
	}
	if (Array.isArray(value)) {
		if (isArrayKeyListPath(path) && isPrimitiveArray(value)) {
			writeLayerValue(layer, pathMap, invertedPathMap, path, value, timestamp, useDurablePaths);
			return;
		}
		const keys = createArrayKeys(value, arrayItemKeyProperty);
		writeLayerValue(layer, pathMap, invertedPathMap, `${path}.${arrayKeysProperty}`, keys, timestamp, useDurablePaths);
		value.forEach((item, index) => {
			const key = keys[index];
			if (!key) return;
			if (isArrayObject(item)) {
				for (const [childPath, childValue] of Object.entries(item)) {
					if (childPath === "__key") continue;
					writeInputValue(layer, pathMap, invertedPathMap, `${path}.${key}.${childPath}`, childValue, timestamp, useDurablePaths);
				}
				return;
			}
			if (isPrimitive(item)) writeLayerValue(layer, pathMap, invertedPathMap, `${path}.${key}`, item, timestamp, useDurablePaths);
		});
		return;
	}
	if (isArrayObject(value)) {
		const entries = Object.entries(value);
		if (entries.length === 0) {
			writeLayerValue(layer, pathMap, invertedPathMap, path, anvilEmptyObjectValue, timestamp, useDurablePaths);
			return;
		}
		for (const [childPath, childValue] of entries) {
			if (childPath === "__key") continue;
			writeInputValue(layer, pathMap, invertedPathMap, `${path}.${childPath}`, childValue, timestamp, useDurablePaths);
		}
	}
};
/**
* Builds one store layer from public input. This is the boundary where nested
* values, object arrays, and path strings are normalized into the flat layer
* map used by rollups and persistence.
*/
const createLayerFromValues = (values, pathMap, invertedPathMap, options = {}) => {
	const timestamp = getLayerTimestamp(options.timestamp);
	const layer = createLayer(timestamp);
	const useDurablePaths = options.useDurablePaths ?? true;
	for (const [path, value] of Object.entries(values)) writeInputValue(layer, pathMap, invertedPathMap, path, value, timestamp, useDurablePaths);
	return layer;
};
/**
* Creates the default interaction and validation state for a field registration
* before any user input, blur, validation, or manual issue has occurred.
*/
const createFieldState = (validation) => ({
	validation,
	touched: false,
	dirty: false,
	userTouched: false,
	userDirty: false,
	isValid: true,
	errors: [],
	warnings: []
});
/**
* Produces a detached form state snapshot so subscribers and submit handlers
* cannot mutate the store's live state object.
*/
const cloneAnvilState = (state) => structuredClone(state);
//#endregion
//#region src/core/anvil-store.ts
const defaultAnvilState = {
	hasChanges: false,
	isDirty: false,
	isTouched: false,
	isValid: true,
	isValidating: false,
	isSubmitting: false,
	isSubmitted: false,
	isSubmitSuccessful: false,
	isLoading: false,
	disabled: false,
	submitCount: 0,
	fieldState: {},
	errors: {},
	warnings: {}
};
/**
* Creates a fresh mutable state object from the default template so stores never
* share field state, issue maps, or submit flags.
*/
const createDefaultAnvilState = () => cloneAnvilState(defaultAnvilState);
const layerTieBreakPriority = {
	l1: 1,
	l2: 2,
	l3: 3,
	l4: 4
};
const deletePathEntries = (entries, path, changedPaths) => {
	for (const entryPath of Object.keys(entries)) {
		if (!isPathPrefixMatch(entryPath, path)) continue;
		delete entries[entryPath];
		changedPaths.add(entryPath);
	}
};
/**
* Web form state alignment:
*
* Dirty: HTML defines a dirty value flag for `input` and `textarea` controls.
* The flag becomes true when the user changes the control value, and it can
* also become true when the value IDL attribute is changed programmatically.
* For Anvil, `dirty` means the stored value no longer matches the default
* layer value, while `userDirty` narrows that to user-originated l4 writes.
*
* Touched: HTML does not define a general touched flag for form controls.
* Anvil uses the framework convention that a field is touched after it has
* received user interaction or writes, with `userTouched` reserved for
* user-originated l4 writes. React wrappers can later map this to focus/blur
* event capture if they need stricter UI-level touch semantics.
*
* Constructor value inputs seed layers only:
* - defaultValues -> l1
* - initialValues -> l2
* - localValues -> l3
*
*
* l4 are the user entered values they are in memory only.
*
* References:
* - https://html.spec.whatwg.org/multipage/input.html#concept-input-value-dirty-flag
* - https://html.spec.whatwg.org/multipage/form-elements.html#concept-textarea-dirty
*/
var AnvilStore = class {
	events;
	eventFieldStates = /* @__PURE__ */ new Map();
	eventFormState;
	state = createDefaultAnvilState();
	layers;
	formLayer;
	fieldObservers = [];
	changeObservers = [];
	batchDepth = 0;
	pendingFieldNotifications = [];
	pendingChangeNotification = false;
	/**
	* Groups synchronous writes so subscribers only observe the completed edit.
	* Reads inside the callback see writes immediately. This batches publication,
	* not rollback: validate commands before entering the batch. Nested batches
	* publish at the outer boundary, including when a callback throws.
	*/
	batch(work) {
		this.batchDepth += 1;
		try {
			work();
		} finally {
			this.batchDepth -= 1;
			if (this.batchDepth === 0) {
				const notifications = this.pendingFieldNotifications;
				const notifyChange = this.pendingChangeNotification;
				this.pendingFieldNotifications = [];
				this.pendingChangeNotification = false;
				for (const notification of notifications) this._notifyFieldSubscribers(notification.paths, notification.layers);
				if (notifyChange) this._notifyChangeSubscribers();
			}
		}
	}
	formValue = {};
	valueChangesNeedUpdate = true;
	cachedHasChanges = false;
	pathMap = {};
	invertedPathMap = {};
	pathSegmentsMap = /* @__PURE__ */ new Map();
	ignoredFieldPaths = /* @__PURE__ */ new Set();
	ignoredFieldPathCache = /* @__PURE__ */ new Map();
	/**
	* Seeds the four value layers, builds the initial rolled-up form layer, and
	* derives aggregate state flags from the seeded data.
	*/
	constructor(values = {}) {
		this.events = new AnvilEvents(values.events);
		this.layers = {
			l1: createLayerFromValues(values.defaultValues ?? {}, this.pathMap, this.invertedPathMap),
			l2: createLayerFromValues(values.initialValues ?? {}, this.pathMap, this.invertedPathMap, { timestamp: values.initialValuesUpdatedAt }),
			l3: createLayerFromValues(values.localValues ?? {}, this.pathMap, this.invertedPathMap),
			l4: createLayer()
		};
		this.formLayer = createLayer();
		this._rollupLayer();
		this._updateFormStateFlags();
		this.events.run("store.initialize", "system", () => {
			for (const layer of [
				"l1",
				"l2",
				"l3",
				"l4"
			]) {
				this.events.record("layer", "", void 0, this.layers[layer]._d, layer);
				for (const [key, value] of Object.entries(this.layers[layer]._l)) this.events.record("value", this._getKey(key), void 0, value, layer);
			}
			this._captureFormState();
		});
	}
	/**
	* Refreshes derived state flags after construction or external layer setup.
	*/
	flush() {
		this.valueChangesNeedUpdate = true;
		this._updateFormStateFlags();
	}
	/**
	* Subscribes to debounced whole-store value snapshots.
	*
	* This is intended for persistence, autosave, diagnostics, and framework
	* adapters that need a single rolled-up values payload after store changes.
	*/
	subscribe(observer, options = {}) {
		const token = Symbol("anvil-store-change-observer");
		this.changeObservers.push({
			observer,
			debounceMs: options.debounceMs ?? 0,
			layer: options.layer,
			timer: void 0,
			token
		});
		return { unsubscribe: () => {
			for (const entry of this.changeObservers) if (entry.token === token && entry.timer) clearTimeout(entry.timer);
			this.changeObservers = this.changeObservers.filter((entry) => entry.token !== token);
		} };
	}
	/**
	* Publishes the current rolled-up values through whole-store subscriptions.
	* Form wrappers use this for wrapper-level events that alter derived form
	* metadata but do not directly write a store value.
	*/
	notifySubscribers() {
		this._notifyChangeSubscribers();
	}
	/**
	* Subscribes to updates for one field path.
	*
	* Exact subscriptions only publish when the changed path matches `path`.
	* Pass `{ includeChildren: true }` to also publish for descendant paths, which
	* lets callers watch a group like `profile` while individual fields such as
	* `profile.name` change. Pass `layer` to read and react only to changes from
	* that source layer, including changes hidden by a higher-priority layer.
	*/
	subscribeField(path, observer, options = {}) {
		const token = Symbol("anvil-store-field-observer");
		this.fieldObservers.push({
			observer,
			path,
			resolvedPath: this._resolveSubscriptionPath(path, options.layer),
			includeChildren: options.includeChildren ?? false,
			layer: options.layer,
			token
		});
		return { unsubscribe: () => {
			this.fieldObservers = this.fieldObservers.filter((entry) => entry.token !== token);
		} };
	}
	/**
	* Convenience wrapper for subscribing to a field path and all descendants.
	*/
	subscribeFieldPrefix(path, observer, options = {}) {
		return this.subscribeField(path, observer, {
			includeChildren: true,
			layer: options.layer
		});
	}
	/**
	* Registers field metadata and validation without requiring a rendered field.
	* This gives Core a known field state before value, validation, or submit work.
	*/
	register(path, validation, options = {}) {
		return this.events.run("store.register", "system", () => {
			const wasIgnored = this._setFieldIgnored(path, false);
			let hasRegistrationChange = wasIgnored;
			const existingFieldState = this.state.fieldState[path];
			if (wasIgnored) {
				this.state.fieldState[path] = createFieldState(validation);
				this.state.errors[path] = [];
				this.state.warnings[path] = [];
			} else if (existingFieldState) {
				if (existingFieldState.validation !== validation) {
					existingFieldState.validation = validation;
					hasRegistrationChange = true;
				}
			} else {
				this.state.fieldState[path] = createFieldState(validation);
				hasRegistrationChange = true;
			}
			if (!this.state.errors[path]) {
				this.state.errors[path] = [];
				hasRegistrationChange = true;
			}
			if (!this.state.warnings[path]) {
				this.state.warnings[path] = [];
				hasRegistrationChange = true;
			}
			if (hasRegistrationChange) {
				const changedPaths = wasIgnored ? this._rollupIgnoredField(path) : [];
				this._updateFormStateFlags();
				this._notifyFieldSubscribers([...new Set([path, ...changedPaths])]);
				this._notifyChangeSubscribers();
			}
			return (unregisterOptions = {}) => {
				this.unregister(path, {
					...unregisterOptions,
					ignore: unregisterOptions.ignore ?? options.ignoreWhenUnregistered ?? false
				});
			};
		});
	}
	/**
	* Marks a field as ignored or active and optionally removes transient state,
	* issues, warnings, or l3/l4 values for the path and its descendants.
	*/
	unregister(path, options = {}) {
		return this.events.run("store.unregister", "system", () => {
			const shouldIgnore = options.ignore ?? true;
			const changedPaths = new Set([path]);
			const ignoredChanged = this._setFieldIgnored(path, shouldIgnore);
			if (options.removeValues) for (const changedPath of this._deleteValues(path)) changedPaths.add(changedPath);
			else if (ignoredChanged) for (const changedPath of this._rollupIgnoredField(path)) changedPaths.add(changedPath);
			if (options.removeState) deletePathEntries(this.state.fieldState, path, changedPaths);
			if (options.removeErrors) deletePathEntries(this.state.errors, path, changedPaths);
			if (options.removeWarnings) deletePathEntries(this.state.warnings, path, changedPaths);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers([...changedPaths]);
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Reports whether validation, aggregate state, and transient values should
	* skip a path. Cache ancestor lookups until ignored membership changes.
	*/
	isFieldIgnored(path) {
		if (this.ignoredFieldPaths.size === 0) return false;
		const resolvedPath = this._resolveArrayPath(path, this.formLayer._l);
		const cached = this.ignoredFieldPathCache.get(resolvedPath);
		if (cached !== void 0) return cached;
		let candidatePath = resolvedPath;
		while (candidatePath) {
			if (this.ignoredFieldPaths.has(candidatePath)) {
				this.ignoredFieldPathCache.set(resolvedPath, true);
				return true;
			}
			const separatorIndex = candidatePath.lastIndexOf(".");
			if (separatorIndex === -1) break;
			candidatePath = candidatePath.slice(0, separatorIndex);
		}
		this.ignoredFieldPathCache.set(resolvedPath, false);
		return false;
	}
	_setFieldIgnored(path, ignored) {
		const resolvedPath = this._resolveArrayPath(path, this.formLayer._l);
		if (this.ignoredFieldPaths.has(resolvedPath) === ignored) return false;
		if (ignored) this.ignoredFieldPaths.add(resolvedPath);
		else this.ignoredFieldPaths.delete(resolvedPath);
		this.events.record("ignored", resolvedPath, !ignored, ignored);
		this.ignoredFieldPathCache.clear();
		return true;
	}
	/** Ignoring a path only changes values when a retained draft exists below it. */
	_rollupIgnoredField(path) {
		const resolvedPath = this._resolveArrayPath(path, this.formLayer._l);
		for (const layer of [this.layers.l3, this.layers.l4]) if (Object.keys(layer._l).some((key) => isPathPrefixMatch(this._getKey(key), resolvedPath))) return this._rollupLayer();
		return [];
	}
	/**
	* Preserves the current public check used by consumers to test ignored status.
	*/
	isActiveField(path) {
		return this.isFieldIgnored(path);
	}
	/**
	* Runs a first submit pass: locks field changes, validates registered fields,
	* calls the submit handler with immutable snapshots when valid, and unlocks
	* the form once submit work finishes.
	*/
	async submit(onSubmit, options = {}) {
		const context = this.events.run("store.submit", "user", () => {
			this.state.isSubmitting = true;
			this.state.disabled = true;
			this._notifyChangeSubscribers();
			return this.events.captureContext();
		});
		try {
			if (!options.skipValidation) this.events.withContext(context, () => this._validateRegisteredFields());
			if (!options.skipValidation && !this.state.isValid) {
				this.events.withContext(context, () => this.events.run("store.submit.invalid", "system", () => {
					this.state.submitCount += 1;
					this.state.isSubmitted = true;
					this.state.isSubmitSuccessful = false;
					this._notifyChangeSubscribers();
				}));
				return;
			}
			const res = await onSubmit(Object.freeze(this.getFormValue()), this._createStateSnapshot());
			this.events.withContext(context, () => this.events.run("store.submit.success", "system", () => {
				this.state.submitCount += 1;
				this.state.isSubmitted = true;
				this.state.isSubmitSuccessful = this.state.isValid;
				this._notifyChangeSubscribers();
			}));
			return res;
		} catch (error) {
			this.events.withContext(context, () => this.events.run("store.submit.error", "system", () => {
				this.state.isSubmitSuccessful = false;
				this._notifyChangeSubscribers();
			}));
			throw error;
		} finally {
			this.events.withContext(context, () => this.events.run("store.submit.complete", "system", () => {
				this.state.isSubmitting = false;
				this.state.disabled = false;
				this._notifyChangeSubscribers();
			}));
		}
	}
	/**
	* Runs the validation definition for one field and updates field errors plus
	* aggregate validity flags. Function validators may return false or a string.
	*/
	validate(path, validation) {
		return this.events.run("store.validate", "system", () => {
			if (this.isFieldIgnored(path)) return;
			const fieldState = this._ensureFieldState(path, validation);
			this.state.isValidating = true;
			try {
				const issue = this._runValidation(path, validation);
				const retained = (this.state.errors[path] ?? []).filter((error) => error.code !== "validate");
				this.state.errors[path] = issue ? [...retained, issue] : retained;
				fieldState.isValid = this.state.errors[path].length === 0;
			} finally {
				this.state.isValidating = false;
				this._captureFieldState(path);
				this._updateFormStateFlags();
				this._notifyFieldSubscribers(path);
				this._notifyChangeSubscribers();
			}
		});
	}
	/**
	* Sets field touched state and publishes scoped field updates.
	*/
	setFieldTouched(path, touched) {
		return this.events.run("store.setFieldTouched", "system", () => {
			const fieldState = this._ensureFieldState(path, void 0);
			fieldState.touched = touched;
			this._captureFieldState(path);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(path);
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Sets or clears a manual field error and publishes scoped field updates.
	*/
	setFieldError(path, message, options = {}) {
		return this.events.run("store.setFieldError", "system", () => {
			const fieldState = this._ensureFieldState(path, void 0);
			const retained = options.retainValidationErrors ? (this.state.errors[path] ?? []).filter((issue) => issue.code !== "manual") : [];
			if (message) {
				this.state.errors[path] = [...retained, {
					path,
					code: "manual",
					message,
					source: "manual",
					metadata: {}
				}];
				fieldState.isValid = false;
			} else {
				this.state.errors[path] = retained;
				fieldState.isValid = retained.length === 0;
			}
			this._captureFieldState(path);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(path);
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Sets or clears a non-blocking field warning and publishes scoped field
	* updates.
	*/
	setFieldWarning(path, message) {
		return this.events.run("store.setFieldWarning", "system", () => {
			this._ensureFieldState(path, void 0);
			if (message) this.state.warnings[path] = [{
				path,
				code: "manual",
				message,
				source: "manual",
				metadata: {}
			}];
			else this.state.warnings[path] = [];
			this._captureFieldState(path);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(path);
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Reads from one layer. Exact paths return primitive store values; parent paths
	* return an unflattened object built from matching child paths.
	*
	*
	* @todo this con be simplified and improved further
	*/
	getValue(path, layer) {
		if (!layer) {
			const value = traverseObjectPath(this._getFormValuePath(path), this.formValue, true);
			if (isObject$1(value) && Object.hasOwn(value, "__key")) {
				const { [arrayItemKeyProperty]: _arrayItemKey, ...itemValue } = value;
				return itemValue;
			}
			if (value !== void 0) return value;
		}
		return this._getValueFromLayer(path, this._getLayerMap(layer), !layer);
	}
	/**
	* Reads retained drafts for structural edits such as moving group items.
	* Conditional logic and submission should use the normal effective reads.
	*/
	getDraftValue(path) {
		const draftValues = this._getDraftLayerValues();
		if (draftValues === this.formLayer._l) return this.getValue(path);
		return this._getValueFromLayer(path, draftValues);
	}
	/** Resolve keyed or mixed array paths to the current public indexes. */
	getIndexedPath(path) {
		return this._getFormValuePath(path);
	}
	_getValueFromLayer(path, layerValues, useCachedFormValue = false) {
		const resolvedPath = this._resolveArrayPathWithSegments(path, layerValues);
		if (!this._isArrayPathVisible(resolvedPath.segments, layerValues)) return;
		const exactValue = layerValues[this._getExistingDurableKey(resolvedPath.path)];
		if (exactValue) {
			if (isAnvilDeletedValue(exactValue._v)) return;
			if (!isAnvilEmptyObjectValue(exactValue._v)) return exactValue._v;
		}
		if (useCachedFormValue) return exactValue ? {} : void 0;
		const prefixValue = this._getPrefixValue(layerValues, resolvedPath.path, resolvedPath.segments.length);
		if (!prefixValue.found) return exactValue ? {} : void 0;
		return prefixValue.value;
	}
	/**
	* Writes a value into a layer and refreshes derived state. Related parent or
	* child path collisions are ignored for now to avoid ambiguous flat-map data.
	* User writes also update user-specific touched and dirty flags.
	*/
	setValue(path, value, source, layer = "l4") {
		return this.events.run("store.setValue", source, () => {
			const writeResult = this._writeLayerValue(path, value, layer);
			if (!writeResult) return;
			const fieldState = this._ensureFieldState(path, void 0);
			fieldState.touched = true;
			fieldState.dirty = !this._isDefaultValue(writeResult.resolvedPath, value);
			if (source === "user" && layer === "l4") {
				fieldState.userTouched = true;
				fieldState.userDirty = fieldState.dirty;
			}
			this._captureFieldState(path);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(writeResult.changedPaths, this._createLayerFieldChanges(layer, writeResult.layerChangedPaths));
			this._notifyChangeSubscribers();
		});
	}
	deleteValue(path, source, layer = "l4") {
		return this.events.run("store.deleteValue", source, () => {
			const resolvedPath = this._resolveArrayPath(path, this.formLayer._l);
			const writeResult = this._writeDeletedLayerValue(resolvedPath, layer);
			const fieldState = this._ensureFieldState(path, void 0);
			fieldState.touched = true;
			fieldState.dirty = true;
			if (source === "user" && layer === "l4") {
				fieldState.userTouched = true;
				fieldState.userDirty = true;
			}
			this._captureFieldState(path);
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(writeResult.changedPaths, this._createLayerFieldChanges(layer, writeResult.layerChangedPaths));
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Writes directly to a source layer without marking the field as touched or
	* dirty, which is used for defaults, initial values, and cache hydration.
	*/
	setLayerValue(path, value, layer) {
		return this.events.run("store.setLayerValue", "system", () => {
			const writeResult = this._writeLayerValue(path, value, layer);
			if (!writeResult) return;
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(writeResult.changedPaths, this._createLayerFieldChanges(layer, writeResult.layerChangedPaths));
			this._notifyChangeSubscribers();
		});
	}
	/**
	* Deletes values from selected layers and publishes every public path whose
	* rolled-up value changed as a result.
	*/
	deleteLayerValues(pathOrPaths, layers) {
		return this.events.run("store.deleteLayerValues", "system", () => {
			const deletedPaths = /* @__PURE__ */ new Set();
			const layerChangedPathSets = {};
			const paths = Array.isArray(pathOrPaths) ? pathOrPaths : [pathOrPaths];
			for (const path of paths) for (const layer of layers) {
				const resolvedPath = this._resolveArrayPath(path, this.layers[layer]._l);
				const deletedLayerPaths = this._deletePrefixFromLayer(layer, resolvedPath);
				for (const deletedPath of deletedLayerPaths) deletedPaths.add(deletedPath);
				if (deletedLayerPaths.length > 0) {
					let layerChangedPaths = layerChangedPathSets[layer];
					if (!layerChangedPaths) {
						layerChangedPaths = /* @__PURE__ */ new Set();
						layerChangedPathSets[layer] = layerChangedPaths;
					}
					layerChangedPaths.add(resolvedPath);
				}
			}
			if (deletedPaths.size === 0) return [];
			const formChangedPaths = this._rollupLayer();
			for (const changedPath of formChangedPaths) deletedPaths.add(changedPath);
			const changedPaths = [...deletedPaths];
			const layerChangedPaths = {};
			for (const layer of layers) {
				const changedLayerPaths = layerChangedPathSets[layer];
				if (changedLayerPaths) layerChangedPaths[layer] = [...changedLayerPaths];
			}
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(changedPaths, layerChangedPaths);
			this._notifyChangeSubscribers();
			return changedPaths;
		});
	}
	/**
	* Replaces one whole value layer, updates its timestamp, and recomputes
	* the rolled-up form layer. Effective-value subscribers receive rolled-up
	* changes, while subscriptions scoped to this source layer receive its own
	* changed paths even when a higher-priority value hides them.
	*/
	setLayer(layer = "l4", data, options = { notifySubscribers: true }) {
		return this.events.run("store.setLayer", "system", () => {
			const nextLayer = createLayer();
			for (const [path, value] of Object.entries(data._l)) nextLayer._l[this._getDurableKey(path)] = value;
			nextLayer._d = data._d;
			const layerChangedPaths = this._getChangedLayerKeys(this.layers[layer]._l, nextLayer._l);
			for (const key of new Set([...Object.keys(this.layers[layer]._l), ...Object.keys(nextLayer._l)])) this.events.record("value", this._getKey(key), this.layers[layer]._l[key], nextLayer._l[key], layer);
			this.events.record("layer", "", this.layers[layer]._d, nextLayer._d, layer);
			this.layers[layer] = nextLayer;
			const changedPaths = this._rollupLayer();
			this._updateFormStateFlags();
			this._notifyFieldSubscribers(changedPaths, this._createLayerFieldChanges(layer, layerChangedPaths));
			if (options.notifySubscribers) this._notifyChangeSubscribers();
		});
	}
	/**
	* Returns one flat value layer as a nested object suitable for submit payloads
	* or UI consumers that expect object-shaped form values.
	*/
	getFormValue(layer) {
		if (!layer) return this.formValue;
		return this._createFormValue(layer);
	}
	/**
	* Keeps hidden drafts in local persistence even when the exposed form has no
	* changes. Uses the same timestamp and deletion rules as normal rollups.
	*/
	getPersistenceSnapshot() {
		const layerValues = this._getDraftLayerValues();
		return {
			values: structuredClone(this._getPathValueMap(void 0, { layerValues })),
			hasChanges: this._hasInitialValueChanges(layerValues)
		};
	}
	/** Replaces interaction state without losing reset changes from history. */
	replaceState(state) {
		return this.events.run("store.replaceState", "system", () => {
			const paths = new Set([
				...Object.keys(this.state.fieldState),
				...Object.keys(state.fieldState),
				...this.eventFieldStates.keys()
			]);
			this.state = state;
			for (const path of paths) this._captureFieldState(path);
			this._captureFormState();
		});
	}
	_setLayerTimestamp(layer, timestamp) {
		this.events.record("layer", "", this.layers[layer]._d, timestamp, layer);
		this.layers[layer]._d = timestamp;
	}
	_setLayerEntry(layer, key, value) {
		this.events.record("value", this._getKey(key), this.layers[layer]._l[key], value, layer);
		if (value === void 0) delete this.layers[layer]._l[key];
		else this.layers[layer]._l[key] = value;
	}
	_captureFieldState(path) {
		const field = this.state.fieldState[path];
		const next = field || this.state.errors[path] || this.state.warnings[path] ? {
			touched: field?.touched,
			dirty: field?.dirty,
			userTouched: field?.userTouched,
			userDirty: field?.userDirty,
			isValid: field?.isValid,
			hasValidation: field?.validation !== void 0,
			errors: this.state.errors[path],
			warnings: this.state.warnings[path]
		} : void 0;
		if (!this.events.record("field", path, this.eventFieldStates.get(path), next)) return;
		if (next) this.eventFieldStates.set(path, structuredClone(next));
		else this.eventFieldStates.delete(path);
	}
	_captureFormState() {
		const { fieldState: _fields, errors: _errors, warnings: _warnings, ...next } = this.state;
		this.events.record("state", "", this.eventFormState, next);
		this.eventFormState = next;
	}
	_getDraftLayerValues() {
		return !(this.ignoredFieldPaths.size > 0 && [this.layers.l3, this.layers.l4].some((layer) => Object.keys(layer._l).some((key) => this.isFieldIgnored(this._getKey(key))))) ? this.formLayer._l : this._performRollupLayer(this.layers.l1, this.layers.l2, this.layers.l3, this.layers.l4, true).layer._l;
	}
	_createFormValue(layer) {
		const value = unflattenStoreValues(this._getSegmentedPathValues(layer));
		if (isObject$1(value)) return value;
		return {};
	}
	/**
	* Replaces the cached rolled-up form layer and returns the public paths whose
	* effective value changed.
	*/
	_rollupLayer() {
		this.valueChangesNeedUpdate = true;
		const { paths, layer } = this._performRollupLayer();
		this.formLayer = layer;
		if (paths.length > 0) this.formValue = this._createFormValue();
		return paths;
	}
	/**
	* Updates one already-visible primitive without rebuilding the full form
	* layer or unflattening every field.
	*/
	_rollupLayerValue(path, durablePath) {
		this.valueChangesNeedUpdate = true;
		const previousValue = this.formLayer._l[durablePath];
		const effectiveValue = this._getEffectiveLayerValue(durablePath);
		if (!effectiveValue || isObject$1(effectiveValue.value._v)) return this._rollupLayer();
		if (this.areStoreValuesEqual(previousValue, effectiveValue.value)) return [];
		const nextFormValue = replaceValueAtPath(this.formValue, this._getFormValuePath(path), effectiveValue.value._v);
		if (nextFormValue === this.formValue) return this._rollupLayer();
		this.formLayer._l[durablePath] = effectiveValue.value;
		this.formLayer._d = Date.now();
		this.formValue = nextFormValue;
		return [path];
	}
	_getEffectiveLayerValue(path, lay1 = this.layers.l1, lay2 = this.layers.l2, lay3 = this.layers.l3, lay4 = this.layers.l4, includeIgnored = false) {
		const l2 = lay2._l[path];
		const ignoreTransient = !includeIgnored && (lay3._l[path] !== void 0 || lay4._l[path] !== void 0) && this.isFieldIgnored(this._getKey(path));
		const l3 = ignoreTransient ? void 0 : lay3._l[path];
		const l4 = ignoreTransient ? void 0 : lay4._l[path];
		let middleValue;
		if (l3 && l4) middleValue = l3._d > l4._d ? {
			layer: "l3",
			value: l3
		} : {
			layer: "l4",
			value: l4
		};
		else if (l4) middleValue = {
			layer: "l4",
			value: l4
		};
		else if (l3) middleValue = {
			layer: "l3",
			value: l3
		};
		if (l2 && middleValue) return l2._d > middleValue.value._d ? {
			layer: "l2",
			value: l2
		} : middleValue;
		if (middleValue) return middleValue;
		if (l2) return {
			layer: "l2",
			value: l2
		};
		const l1Value = lay1._l[path];
		return l1Value ? {
			layer: "l1",
			value: l1Value
		} : void 0;
	}
	/**
	* Rebuilds the exposed form layer from l1-l4 and returns only paths whose
	* effective value changed. Source-layer updates call this before notifying
	* field subscribers so hidden lower-layer changes do not wake UI listeners.
	*/
	_performRollupLayer(lay1 = this.layers.l1, lay2 = this.layers.l2, lay3 = this.layers.l3, lay4 = this.layers.l4, includeIgnored = false) {
		const nextLayer = createLayer();
		const nextValues = { ...lay1._l };
		const nextValueSources = {};
		for (const path of Object.keys(lay1._l)) nextValueSources[path] = "l1";
		const middleLayerKeys = new Set([
			...Object.keys(lay2._l),
			...Object.keys(lay3._l),
			...Object.keys(lay4._l)
		]);
		for (const path of middleLayerKeys) {
			const effectiveValue = this._getEffectiveLayerValue(path, lay1, lay2, lay3, lay4, includeIgnored);
			if (effectiveValue) {
				nextValues[path] = effectiveValue.value;
				nextValueSources[path] = effectiveValue.layer;
			}
		}
		nextLayer._l = this._withDeletedValuesApplied(nextValues, nextValueSources);
		nextLayer._d = Date.now();
		return {
			layer: nextLayer,
			paths: this._getChangedLayerKeys(this.formLayer._l, nextLayer._l)
		};
	}
	/**
	* Normalizes and writes one public value into a source layer, expanding object
	* and array values into flat durable-keyed entries when needed. The write is
	* intentionally staged: resolve the public path, normalize the value, choose
	* the cheapest safe write, preserve replacement semantics with tombstones,
	* and only then rebuild the effective form layer.
	*/
	_writeLayerValue(path, value, layer) {
		const layerValues = this.formLayer._l;
		const resolvedPath = this._resolveArrayPath(path, layerValues);
		const hasLayerValueChanged = !deepEqual(this.getValue(path, layer), value);
		if (this._shouldClearNormalizedArray(resolvedPath, value)) return this._writeEmptyNormalizedArray(resolvedPath, layer, hasLayerValueChanged);
		const normalizedLayer = createLayerFromValues({ [resolvedPath]: value }, this.pathMap, this.invertedPathMap);
		const normalizedEntries = Object.entries(normalizedLayer._l);
		if (normalizedEntries.length === 0) return;
		const incrementalEntry = this._getIncrementalRollupEntry(resolvedPath, normalizedEntries, layer);
		if (incrementalEntry) {
			const [durablePath, storeValue] = incrementalEntry;
			this._setLayerEntry(layer, durablePath, storeValue);
			this._setLayerTimestamp(layer, Date.now());
			return {
				changedPaths: this._rollupLayerValue(resolvedPath, durablePath),
				layerChangedPaths: hasLayerValueChanged ? [resolvedPath] : [],
				resolvedPath
			};
		}
		const isEmptyObjectWrite = normalizedEntries.some(([durablePath, storeValue]) => this._getKey(durablePath) === resolvedPath && isAnvilEmptyObjectValue(storeValue._v));
		const isExpandedValue = isEmptyObjectWrite || normalizedEntries.length > 1 || normalizedEntries.some(([durablePath]) => this._getKey(durablePath) !== resolvedPath);
		const descendantPaths = this._getDescendantPaths(resolvedPath);
		const isLeafSubtreeReplacement = normalizedEntries.length === 1 && !isExpandedValue && descendantPaths.length > 0;
		if (!isExpandedValue && !isLeafSubtreeReplacement && !this._clearEmptyObjectAncestorCollisions(resolvedPath, layer)) return;
		const removedPaths = isEmptyObjectWrite || isLeafSubtreeReplacement ? descendantPaths : isExpandedValue ? this._getRemovedPrefixPaths(resolvedPath, normalizedEntries) : [];
		if (isExpandedValue || isLeafSubtreeReplacement) this._deletePrefixFromLayer(layer, resolvedPath);
		for (const [durablePath, storeValue] of normalizedEntries) this._setLayerEntry(layer, durablePath, storeValue);
		this._setDeletedLayerValues(removedPaths, layer);
		this._setLayerTimestamp(layer, Date.now());
		return {
			changedPaths: this._rollupLayer(),
			layerChangedPaths: hasLayerValueChanged ? [resolvedPath] : [],
			resolvedPath
		};
	}
	/**
	* Limits the incremental rollup to an existing visible primitive. Complex
	* values and path collisions retain the full replacement semantics above.
	*/
	_getIncrementalRollupEntry(path, normalizedEntries, layer) {
		if (normalizedEntries.length !== 1) return;
		const entry = normalizedEntries[0];
		if (!entry) return;
		const [durablePath, nextValue] = entry;
		const currentValue = this.formLayer._l[durablePath];
		if (this._getKey(durablePath) !== path || !currentValue || isObject$1(currentValue._v) || isObject$1(nextValue._v) || !deepEqual(this.getValue(path), currentValue._v) || this._hasRelatedLayerPath(path, layer)) return;
		return entry;
	}
	/**
	* Detects an explicit empty array write so the store can clear prior keyed
	* children and retain an empty `__arrayKeys` marker.
	*/
	_shouldClearNormalizedArray(path, value) {
		const segments = parsePath(path);
		const lastSegment = segments[segments.length - 1];
		return Array.isArray(value) && value.length === 0 && lastSegment !== "__arrayKeys";
	}
	/**
	* Writes an empty normalized array marker after deleting all existing item
	* branches under the array path.
	*/
	_writeEmptyNormalizedArray(path, layer, hasLayerValueChanged) {
		const arrayKeysPath = `${path}.${arrayKeysProperty}`;
		this._deletePrefixFromLayer(layer, path);
		const normalizedLayer = createLayerFromValues({ [arrayKeysPath]: [] }, this.pathMap, this.invertedPathMap);
		const normalizedEntries = Object.entries(normalizedLayer._l);
		for (const [durablePath, storeValue] of normalizedEntries) this._setLayerEntry(layer, durablePath, storeValue);
		if (normalizedEntries.length === 0) return;
		this._setLayerTimestamp(layer, Date.now());
		return {
			changedPaths: this._rollupLayer(),
			layerChangedPaths: hasLayerValueChanged ? [path] : [],
			resolvedPath: path
		};
	}
	/**
	* Guarantees that state operations have a field-state record to mutate.
	* Register, validate, and setValue all use this so unregistered fields can
	* still collect touched, dirty, and validation metadata predictably.
	*/
	_ensureFieldState(path, validation) {
		if (this.state.fieldState[path]) {
			if (validation !== void 0) this.state.fieldState[path].validation = validation;
			return this.state.fieldState[path];
		}
		this.state.fieldState[path] = createFieldState(validation);
		return this.state.fieldState[path];
	}
	/**
	* Delivers field-scoped updates to observers whose exact or prefix
	* subscription matches any changed public path.
	*/
	_notifyFieldSubscribers(changedPathOrPaths, layerChangedPaths) {
		const formChangedPaths = asArray(changedPathOrPaths);
		for (const path of formChangedPaths) this._captureFieldState(path);
		this._captureFormState();
		if (this.batchDepth > 0) {
			this.pendingFieldNotifications.push({
				paths: formChangedPaths,
				layers: layerChangedPaths
			});
			return;
		}
		const eventContext = this.events.captureContext();
		const resolvedChanges = /* @__PURE__ */ new Map();
		for (const entry of this.fieldObservers) {
			const changedPaths = entry.layer && layerChangedPaths ? layerChangedPaths[entry.layer] ?? [] : formChangedPaths;
			for (const changedPath of changedPaths) {
				let layerChanges = resolvedChanges.get(entry.layer);
				if (!layerChanges) {
					layerChanges = /* @__PURE__ */ new Map();
					resolvedChanges.set(entry.layer, layerChanges);
				}
				let resolvedPath = layerChanges.get(changedPath);
				if (resolvedPath === void 0) {
					resolvedPath = this._resolveSubscriptionPath(changedPath, entry.layer);
					layerChanges.set(changedPath, resolvedPath);
				}
				if (this._doesFieldObserverMatchPath(entry, resolvedPath)) this.events.withContext(eventContext, () => entry.observer.next(this._createFieldUpdate(entry.path, changedPath, entry.layer)));
			}
		}
	}
	/**
	* Delivers debounced whole-layer snapshots to store subscribers, replacing any
	* pending timer for the same observer.
	*/
	_notifyChangeSubscribers() {
		this._captureFormState();
		if (this.batchDepth > 0) {
			this.pendingChangeNotification = true;
			return;
		}
		const eventContext = this.events.captureContext();
		for (const entry of this.changeObservers) {
			if (entry.timer) clearTimeout(entry.timer);
			entry.timer = setTimeout(() => {
				entry.timer = void 0;
				this.events.withContext(eventContext, () => entry.observer.next(structuredClone(this._getPathValueMap(entry.layer ?? void 0))));
			}, entry.debounceMs);
		}
	}
	/**
	* Builds a field observer payload containing both the watched path snapshot
	* and the exact changed path details.
	*/
	_createFieldUpdate(path, changedPath, layer) {
		const fieldState = this.state.fieldState[changedPath] ?? createFieldState(void 0);
		return {
			path,
			value: this.getValue(path, layer),
			changedPath,
			changedValue: this.getValue(changedPath, layer),
			fieldState: this._cloneFieldState(fieldState),
			errors: [...this.state.errors[changedPath] ?? []],
			warnings: [...this.state.warnings[changedPath] ?? []]
		};
	}
	/**
	* Creates a read-only state payload for submit handlers and scoped store
	* subscriptions. This avoids structuredClone because field validation can be
	* a function, which is intentional store state but not cloneable.
	*/
	_createStateSnapshot() {
		const errors = {};
		const warnings = {};
		const fieldState = {};
		for (const [path, state] of Object.entries(this.state.fieldState)) {
			if (this.isFieldIgnored(path)) continue;
			fieldState[path] = this._cloneFieldState(state);
		}
		for (const [path, issues] of Object.entries(this.state.errors)) {
			if (this.isFieldIgnored(path)) continue;
			errors[path] = issues.map((issue) => ({
				...issue,
				metadata: { ...issue.metadata }
			}));
		}
		for (const [path, issues] of Object.entries(this.state.warnings)) {
			if (this.isFieldIgnored(path)) continue;
			warnings[path] = issues.map((issue) => ({
				...issue,
				metadata: { ...issue.metadata }
			}));
		}
		return Object.freeze({
			...this.state,
			errors,
			warnings,
			fieldState
		});
	}
	/**
	* Copies a field state while preserving validator references. Validators can
	* be functions, so this keeps snapshots stable without trying to clone
	* executable code.
	*/
	_cloneFieldState(fieldState) {
		return {
			dirty: fieldState.dirty,
			touched: fieldState.touched,
			validation: fieldState.validation,
			userTouched: fieldState.userTouched,
			userDirty: fieldState.userDirty,
			isValid: fieldState.isValid
		};
	}
	/**
	* Matches resolved indexed subscriptions and treats a parent replacement as
	* a change to subscribed descendants.
	*/
	_doesFieldObserverMatchPath(entry, changedPath) {
		if (isPathPrefixMatch(entry.resolvedPath, changedPath)) return true;
		return entry.includeChildren && isPathPrefixMatch(changedPath, entry.resolvedPath);
	}
	/**
	* Precomputes the durable-keyed subscription path once at subscribe time so
	* notification matching does not resolve indexed paths inside hot loops.
	*/
	_resolveSubscriptionPath(path, layer) {
		return this._resolveArrayPath(path, this._getLayerMap(layer));
	}
	_createLayerFieldChanges(layer, changedPaths) {
		const layerChanges = {};
		layerChanges[layer] = changedPaths;
		return layerChanges;
	}
	/**
	* Converts an external form field path into the internal durable key used by
	* layer storage. This keeps layer keys stable even if the form path changes,
	* and creates a durable key on first write when one does not exist yet.
	*/
	_getDurableKey(path) {
		return getOrCreateDurablePath(path, this.pathMap, this.invertedPathMap);
	}
	/**
	* Looks up an already-known durable key without allocating one, which keeps
	* read-only paths from mutating the path map.
	*/
	_getExistingDurableKey(path) {
		return getDurablePath(path, this.invertedPathMap);
	}
	/**
	* Converts an internal durable key back to a form field path. It is used when
	* data leaves the layer store for public reads or validator input values.
	*/
	_getKey(key) {
		return this.pathMap[key] ?? key;
	}
	_getPathSegments(durableKey, fieldPath) {
		const cachedSegments = this.pathSegmentsMap.get(durableKey);
		if (cachedSegments) return cachedSegments;
		const segments = parsePath(fieldPath);
		this.pathSegmentsMap.set(durableKey, segments);
		return segments;
	}
	/**
	* Produces the validator-facing current value map using form field paths. This
	* prevents custom validators from depending on durable keys while still using
	* the same layer-priority rollup as Core internals.
	*/
	_getPathValueMap(layer, options = {}) {
		const layerValues = options.layerValues ?? this._getLayerMap(layer);
		const includeDeleted = options.includeDeleted ?? true;
		const values = {};
		const deletedPaths = includeDeleted ? void 0 : this._getDeletedValuePaths(layerValues);
		for (const [key, value] of Object.entries(layerValues)) {
			const path = this._getKey(key);
			if (deletedPaths && deletedPaths.size > 0 && this._hasPathPrefix(path, deletedPaths)) continue;
			values[path] = value;
		}
		return values;
	}
	_getSegmentedPathValues(layer) {
		const layerValues = layer ? this.layers[layer]._l : this.formLayer._l;
		const values = [];
		const deletedPaths = this._getDeletedValuePaths(layerValues);
		for (const [durableKey, storeValue] of Object.entries(layerValues)) {
			const fieldPath = this._getKey(durableKey);
			if (deletedPaths.size > 0 && this._hasPathPrefix(fieldPath, deletedPaths)) continue;
			values.push({
				pathSegments: this._getPathSegments(durableKey, fieldPath),
				startIndex: 0,
				value: storeValue._v
			});
		}
		return values;
	}
	/**
	* Selects a specific source layer or the rolled-up form layer for path reads.
	*/
	_getLayerMap(layer) {
		return layer ? this.layers[layer]._l : this.formLayer._l;
	}
	_hasRelatedLayerPath(path, layer) {
		return Object.keys(this.layers[layer]._l).some((durableKey) => isPathRelated(path, this._getKey(durableKey)));
	}
	/**
	* Allows a child field to populate an object that was explicitly initialized
	* as empty in the same layer. Other parent/child collisions remain rejected
	* so the flat layer cannot contain ambiguous scalar or object paths.
	*/
	_clearEmptyObjectAncestorCollisions(path, layer) {
		const emptyObjectAncestorKeys = [];
		for (const [durableKey, storeValue] of Object.entries(this.layers[layer]._l)) {
			const fieldPath = this._getKey(durableKey);
			if (!isPathRelated(path, fieldPath)) continue;
			if (!(path.startsWith(`${fieldPath}.`) && isAnvilEmptyObjectValue(storeValue._v))) return false;
			emptyObjectAncestorKeys.push(durableKey);
		}
		for (const durableKey of emptyObjectAncestorKeys) this._setLayerEntry(layer, durableKey, void 0);
		return true;
	}
	/**
	* Removes all entries under a public path from one layer. Whole-array writes
	* use this before inserting normalized `__arrayKeys` and keyed child values.
	*/
	_deletePrefixFromLayer(layer, path) {
		const deletedPaths = [];
		for (const durableKey of Object.keys(this.layers[layer]._l)) {
			const fieldPath = this._getKey(durableKey);
			if (isPathPrefixMatch(fieldPath, path)) {
				this._setLayerEntry(layer, durableKey, void 0);
				deletedPaths.push(fieldPath);
			}
		}
		if (deletedPaths.length > 0) this._setLayerTimestamp(layer, Date.now());
		return deletedPaths;
	}
	/**
	* Removes transient l3/l4 values for a field path during unregister cleanup
	* while preserving defaults and initial values.
	*/
	_deleteValues(path) {
		const deletedPaths = /* @__PURE__ */ new Set();
		const layers = ["l3", "l4"];
		const resolvedPath = this._resolveArrayPath(path, this.formLayer._l);
		for (const layer of layers) for (const deletedPath of this._deletePrefixFromLayer(layer, resolvedPath)) deletedPaths.add(deletedPath);
		for (const changedPath of this._rollupLayer()) deletedPaths.add(changedPath);
		return [...deletedPaths];
	}
	/**
	* Finds child values for a public field prefix by walking the durable-key map.
	* Layer values are keyed by durable ids, so prefix reads must compare against
	* public field paths from `pathMap` before reading the matching durable entry.
	*/
	_getPrefixValue(layerValues, path, pathSegmentCount) {
		const values = this._getSegmentedPrefixValues(layerValues, path, pathSegmentCount);
		if (values.length === 0) return {
			found: false,
			value: void 0
		};
		return {
			found: true,
			value: unflattenStoreValues(values)
		};
	}
	_getSegmentedPrefixValues(layerValues, path, pathSegmentCount) {
		const prefixValues = [];
		const deletedPaths = this._getDeletedValuePaths(layerValues);
		const prefix = `${path}.`;
		for (const [durableKey, fieldPath] of Object.entries(this.pathMap)) {
			if (!fieldPath.startsWith(prefix)) continue;
			const storeValue = layerValues[durableKey];
			if (!storeValue) continue;
			if (deletedPaths.size > 0 && this._hasPathPrefix(fieldPath, deletedPaths)) continue;
			prefixValues.push({
				pathSegments: this._getPathSegments(durableKey, fieldPath),
				startIndex: pathSegmentCount,
				value: storeValue._v
			});
		}
		return prefixValues;
	}
	_getPrefixValues(layerValues, path) {
		const prefixValues = {};
		const deletedPaths = this._getDeletedValuePaths(layerValues);
		const prefix = `${path}.`;
		for (const [durableKey, fieldPath] of Object.entries(this.pathMap)) {
			if (!fieldPath.startsWith(prefix)) continue;
			const value = layerValues[durableKey];
			if (!value) continue;
			if (deletedPaths.size > 0 && this._hasPathPrefix(fieldPath, deletedPaths)) continue;
			prefixValues[fieldPath.slice(prefix.length)] = value;
		}
		return prefixValues;
	}
	/**
	* Resolves public indexed array paths into internal keyed paths. For example,
	* `items.0.label` becomes `items.k0.label` when `items.__arrayKeys[0]` is
	* `k0`; already-keyed paths pass through unchanged.
	*/
	_resolveArrayPath(path, layerValues) {
		if (!numericPathSegmentPattern.test(path)) return path;
		return this._resolveArrayPathWithSegments(path, layerValues).path;
	}
	_resolveArrayPathWithSegments(path, layerValues) {
		const segments = parsePath(path);
		const resolvedSegments = [];
		for (const segment of segments) {
			const index = Number(segment);
			if (Number.isInteger(index) && index >= 0) {
				const arrayKeysPath = `${resolvedSegments.join(".")}.${arrayKeysProperty}`;
				const arrayKeys = this._getArrayKeysForPath(arrayKeysPath, layerValues);
				if (isStringArray(arrayKeys)) {
					const key = arrayKeys[index];
					if (key) {
						resolvedSegments.push(key);
						continue;
					}
				}
			}
			resolvedSegments.push(segment);
		}
		return {
			path: resolvedSegments.join("."),
			segments: resolvedSegments
		};
	}
	/**
	* Reads array keys from the requested layer first and falls back to the form
	* layer so indexed path resolution can work during layer-specific reads.
	*/
	_getArrayKeysForPath(arrayKeysPath, layerValues) {
		const durableKey = this._getExistingDurableKey(arrayKeysPath);
		const layerValue = layerValues[durableKey];
		if (layerValue !== void 0) return layerValue._v;
		return this.formLayer._l[durableKey]?._v;
	}
	_getFormValuePath(path) {
		return getPathWithArrayIndexes(path, (arrayPath) => this._getArrayKeysForPath(`${arrayPath}.${arrayKeysProperty}`, this.formLayer._l));
	}
	/**
	* Prevents reads from stale keyed paths after a whole-array replacement. If a
	* keyed segment is not listed in the current `__arrayKeys`, that item is no
	* longer part of the public array.
	*/
	_isArrayPathVisible(segments, layerValues) {
		const resolvedSegments = [];
		for (const segment of segments) {
			const arrayPath = resolvedSegments.join(".");
			if (arrayPath) {
				const arrayKeys = layerValues[this._getExistingDurableKey(`${arrayPath}.${arrayKeysProperty}`)]?._v;
				if (isStringArray(arrayKeys) && segment !== "__arrayKeys" && !arrayKeys.includes(segment)) return false;
			}
			resolvedSegments.push(segment);
		}
		return true;
	}
	/**
	* Compares previous and next form-layer maps and returns the field paths whose
	* exposed value changed. Store value timestamps are ignored because UI
	* subscribers only need to react to effective value changes.
	*/
	_getChangedLayerKeys(previousValues, nextValues) {
		const changedKeys = [];
		const durableKeys = new Set([...Object.keys(previousValues), ...Object.keys(nextValues)]);
		for (const durableKey of durableKeys) if (!this.areStoreValuesEqual(previousValues[durableKey], nextValues[durableKey])) changedKeys.push(this._getKey(durableKey));
		return changedKeys;
	}
	/**
	* Checks effective field value equality for form-layer diffing. `_d` is
	* intentionally ignored so timestamp-only layer refreshes do not wake UI
	* subscribers when the rendered value is unchanged.
	*/
	areStoreValuesEqual(previousValue, nextValue) {
		return deepEqual(previousValue && Object.hasOwn(previousValue, "_v") ? previousValue?._v : previousValue, nextValue && Object.hasOwn(nextValue, "_v") ? nextValue?._v : nextValue);
	}
	_getRemovedPrefixPaths(resolvedPath, normalizedEntries) {
		const nextPaths = /* @__PURE__ */ new Set();
		const nextAncestors = /* @__PURE__ */ new Set();
		for (const [durablePath] of normalizedEntries) {
			const path = this._getKey(durablePath);
			nextPaths.add(path);
			let separator = path.lastIndexOf(".");
			let ancestor = path;
			while (separator !== -1) {
				ancestor = ancestor.slice(0, separator);
				nextAncestors.add(ancestor);
				separator = ancestor.lastIndexOf(".");
			}
		}
		const removedPaths = [];
		for (const durableKey of Object.keys(this.formLayer._l)) {
			const fieldPath = this._getKey(durableKey);
			if (isPathPrefixMatch(fieldPath, resolvedPath) && !nextAncestors.has(fieldPath) && !this._hasPathPrefix(fieldPath, nextPaths)) removedPaths.push(fieldPath);
		}
		return removedPaths;
	}
	_getDescendantPaths(path) {
		return Object.keys(this.formLayer._l).map((durableKey) => this._getKey(durableKey)).filter((fieldPath) => fieldPath !== path && isPathPrefixMatch(fieldPath, path));
	}
	/**
	* Collects every source-layer value currently registered at a path or below
	* it. Exact values are returned first so subtree deletion has deterministic
	* behavior without introducing a tombstone at an unregistered parent path.
	*/
	_getRegisteredValuePaths(path) {
		const descendantPaths = /* @__PURE__ */ new Set();
		let hasExactPath = false;
		for (const layer of [
			"l1",
			"l2",
			"l3",
			"l4"
		]) for (const durableKey of Object.keys(this.layers[layer]._l)) {
			const fieldPath = this._getKey(durableKey);
			if (!isPathPrefixMatch(fieldPath, path)) continue;
			if (fieldPath === path) {
				hasExactPath = true;
				continue;
			}
			descendantPaths.add(fieldPath);
		}
		return [...hasExactPath ? [path] : [], ...descendantPaths];
	}
	_writeDeletedLayerValue(path, layer) {
		const hadLayerValue = this.getValue(path, layer) !== void 0;
		const registeredPaths = this._getRegisteredValuePaths(path);
		this._deletePrefixFromLayer(layer, path);
		for (const registeredPath of registeredPaths) this._setDeletedExactLayerValue(registeredPath, layer);
		if (registeredPaths.length > 0) this._setLayerTimestamp(layer, Date.now());
		return {
			changedPaths: this._rollupLayer(),
			layerChangedPaths: hadLayerValue ? [path] : [],
			resolvedPath: path
		};
	}
	/** Install replacement tombstones with one layer scan, preserving event history. */
	_setDeletedLayerValues(paths, layer) {
		if (paths.length === 0) return;
		const prefixes = new Set(paths);
		for (const durableKey of Object.keys(this.layers[layer]._l)) if (this._hasPathPrefix(this._getKey(durableKey), prefixes)) this._setLayerEntry(layer, durableKey, void 0);
		for (const path of paths) this._setDeletedExactLayerValue(path, layer);
		this._setLayerTimestamp(layer, Date.now());
	}
	_setDeletedExactLayerValue(path, layer) {
		this._setLayerEntry(layer, this._getDurableKey(path), createStoreValue(anvilDeletedValue));
	}
	_withDeletedValuesApplied(values, valueSources) {
		const deletedPaths = this._getDeletedValuePaths(values);
		const nextValues = structuredClone(values);
		if (deletedPaths.size > 0) for (const durableKey of Object.keys(nextValues)) {
			const path = this._getKey(durableKey);
			let pathToCheck = path;
			if (deletedPaths.has(path)) {
				const separatorIndex = path.lastIndexOf(".");
				if (separatorIndex === -1) continue;
				pathToCheck = path.slice(0, separatorIndex);
			}
			if (this._hasPathPrefix(pathToCheck, deletedPaths)) delete nextValues[durableKey];
		}
		this._removeValuesShadowedByVisibleDescendants(nextValues, valueSources);
		return nextValues;
	}
	_removeValuesShadowedByVisibleDescendants(values, valueSources) {
		const shadowedKeys = /* @__PURE__ */ new Set();
		const keysByPath = /* @__PURE__ */ new Map();
		for (const key of Object.keys(values)) keysByPath.set(this._getKey(key), key);
		for (const descendantKey of Object.keys(values)) {
			const descendantValue = values[descendantKey];
			if (!descendantValue || isAnvilDeletedValue(descendantValue._v)) continue;
			const descendantSource = valueSources[descendantKey];
			if (!descendantSource) continue;
			let ancestorPath = this._getKey(descendantKey);
			let separatorIndex = ancestorPath.lastIndexOf(".");
			while (separatorIndex !== -1) {
				ancestorPath = ancestorPath.slice(0, separatorIndex);
				const ancestorKey = keysByPath.get(ancestorPath);
				const ancestorValue = ancestorKey === void 0 ? void 0 : values[ancestorKey];
				const ancestorSource = ancestorKey === void 0 ? void 0 : valueSources[ancestorKey];
				if (ancestorKey !== void 0 && ancestorValue && ancestorSource && !isAnvilDeletedValue(ancestorValue._v) && this._doesDescendantShadowValue(ancestorValue, ancestorSource, descendantValue, descendantSource)) shadowedKeys.add(ancestorKey);
				separatorIndex = ancestorPath.lastIndexOf(".");
			}
		}
		for (const shadowedKey of shadowedKeys) delete values[shadowedKey];
	}
	_doesDescendantShadowValue(value, valueSource, descendantValue, descendantSource) {
		if (valueSource === "l1") return true;
		if (descendantSource === "l1") return false;
		if (descendantValue._d !== value._d) return descendantValue._d > value._d;
		return layerTieBreakPriority[descendantSource] >= layerTieBreakPriority[valueSource];
	}
	_getDeletedValuePaths(layerValues) {
		const deletedPaths = /* @__PURE__ */ new Set();
		for (const [durableKey, value] of Object.entries(layerValues)) if (isAnvilDeletedValue(value._v)) deletedPaths.add(this._getKey(durableKey));
		return deletedPaths;
	}
	/**
	* Checks the candidate and each dotted ancestor against a precomputed set of
	* public paths, avoiding a full layer scan for every value.
	*/
	_hasPathPrefix(path, prefixes) {
		let candidatePath = path;
		while (candidatePath) {
			if (prefixes.has(candidatePath)) return true;
			const separatorIndex = candidatePath.lastIndexOf(".");
			if (separatorIndex === -1) return false;
			candidatePath = candidatePath.slice(0, separatorIndex);
		}
		return false;
	}
	/**
	* Compares l2 initial values with the effective form layer to determine
	* whether the form has persisted/user changes relative to server state.
	*/
	_hasInitialValueChanges(layerValues = this.formLayer._l) {
		const durableKeys = new Set([...Object.keys(this.layers.l2._l), ...Object.keys(layerValues)]);
		for (const durableKey of durableKeys) if (!this.areStoreValuesEqual(this.layers.l2._l[durableKey], layerValues[durableKey]) && !this.areStoreValuesEqual(this.layers.l1._l[durableKey], layerValues[durableKey])) return true;
		return false;
	}
	/**
	* Recomputes aggregate form flags from field-level state and issue maps. It is
	* called after writes, registration, validation, and layer replacement so the
	* public state snapshot remains consistent with the latest mutations.
	*/
	_updateFormStateFlags() {
		const activeFieldStates = Object.entries(this.state.fieldState).filter(([path]) => !this.isFieldIgnored(path)).map(([_path, fieldState]) => fieldState);
		const activeErrors = Object.entries(this.state.errors).filter(([path]) => !this.isFieldIgnored(path)).flatMap(([_path, errors]) => errors);
		this.state.isDirty = activeFieldStates.some((fieldState) => fieldState.dirty);
		if (this.valueChangesNeedUpdate) {
			this.cachedHasChanges = this._hasInitialValueChanges();
			this.valueChangesNeedUpdate = false;
		}
		this.state.hasChanges = this.cachedHasChanges;
		this.state.isTouched = activeFieldStates.some((fieldState) => fieldState.touched);
		this.state.isValid = activeErrors.length === 0 && activeFieldStates.every((fieldState) => fieldState.isValid);
	}
	/**
	* Checks whether a candidate value differs from the default layer baseline.
	* setValue uses this to update dirty flags without exposing layer comparison
	* logic to callers.
	*/
	_isDefaultValue(path, value) {
		const defaultLayerValues = this.layers.l1._l;
		const defaultStoreValue = defaultLayerValues[this._getExistingDurableKey(path)];
		const defaultPrefixValues = this._getPrefixValues(defaultLayerValues, path);
		if (!defaultStoreValue && isEmpty(defaultPrefixValues)) return false;
		return deepEqual(this.getValue(path, "l1"), value);
	}
	/**
	* Runs validation for every known field during submit. This keeps submit flow
	* simple while reusing the same single-field validation path used elsewhere.
	*/
	_validateRegisteredFields() {
		for (const [path, fieldState] of Object.entries(this.state.fieldState)) {
			if (this.isFieldIgnored(path)) continue;
			this.validate(path, fieldState.validation);
		}
	}
	/**
	* Normalizes supported validation definitions into AnvilFieldIssue objects.
	* validate calls this so error formatting stays centralized as Core grows
	* support for more validation sources.
	*/
	_runValidation(path, validation) {
		if (!validation) return;
		if (typeof validation === "function") {
			const result = validation(this.getValue(path), this.getFormValue());
			if (result instanceof Promise) throw new TypeError("Anvil field validators must be synchronous.");
			if (result === true || result === void 0) return;
			if (typeof result === "string") return {
				path,
				code: "validate",
				message: result,
				source: "manual",
				metadata: {}
			};
			if (result === false) return {
				path,
				code: "validate",
				message: "Invalid value",
				source: "manual",
				metadata: {}
			};
		}
	}
};
//#endregion
//#region src/utilities/shallow-diff.ts
/**
* Uses the native own-property check without invoking userland object methods.
*/
const hasOwnProperty = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
/**
* Treats any two callback values as equal so hook registration arguments do not
* churn just because React recreated a function reference.
*/
const isFunctionEqual = (left, right) => typeof left === "function" && typeof right === "function";
/**
* Checks only array shape and first-level entries, matching the shallow
* registration diff semantics used by the React adapters.
*/
const shallowArrayDiff = (left, right) => {
	if (left.length !== right.length) return true;
	return left.some((value, index) => shallowValueDiff(value, right[index]));
};
/**
* Compares one shallow property value. Nested objects are intentionally treated
* as equal so deep config objects do not trigger unnecessary unregister/register
* cycles.
*/
const shallowValueDiff = (left, right) => {
	if (Object.is(left, right)) return false;
	if (typeof left === "function" || typeof right === "function") return !isFunctionEqual(left, right);
	if (Array.isArray(left) || Array.isArray(right)) {
		if (!Array.isArray(left) || !Array.isArray(right)) return true;
		return shallowArrayDiff(left, right);
	}
	if (isObject(left) && isObject(right)) return false;
	return true;
};
/**
* Reports whether two option snapshots differ at the first meaningful layer.
* Hooks use this to keep subscriptions stable without deep-walking large data.
*/
const shallowDiff = (left, right) => {
	if (Object.is(left, right)) return false;
	if (typeof left === "function" || typeof right === "function") return !isFunctionEqual(left, right);
	if (Array.isArray(left) || Array.isArray(right)) {
		if (!Array.isArray(left) || !Array.isArray(right)) return true;
		return shallowArrayDiff(left, right);
	}
	if (!isObject(left) || !isObject(right)) return true;
	const leftKeys = Object.keys(left);
	const rightKeys = Object.keys(right);
	if (leftKeys.length !== rightKeys.length) return true;
	return leftKeys.some((key) => {
		if (!hasOwnProperty(right, key)) return true;
		return shallowValueDiff(Object.getOwnPropertyDescriptor(left, key)?.value, Object.getOwnPropertyDescriptor(right, key)?.value);
	});
};
//#endregion
//#region src/utilities/logging.ts
/**
* Reads the live browser API first so logging can be toggled without a reload,
* then falls back to the persisted value for standalone Anvil consumers.
*/
const isAnvilDebugEnabled = () => {
	try {
		if (typeof window !== "undefined") {
			const enabled = window.__wombat?.logging?.anvil?.enabled;
			if (typeof enabled === "boolean") return enabled;
		}
		if (typeof localStorage === "undefined") return false;
		return localStorage.getItem(anvilDebugStorageKey) === "true";
	} catch {
		return false;
	}
};
/**
* Centralizes Anvil diagnostics behind the runtime browser logging setting so
* verbose form/store logging can be enabled without scattering console calls.
*/
const debugAnvil = (event, getDetails) => {
	if (!isAnvilDebugEnabled()) return;
	console.debug("[anvil-form]", event, getDetails?.());
};
//#endregion
//#region src/utilities/unset.ts
const pathSegmentPattern = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
const escapedCharacterPattern = /\\(\\)?/g;
/**
* Converts Lodash-compatible dot and bracket notation into property segments.
*/
const parseUnsetPath = (path) => {
	if (typeof path !== "string") return path.map(String);
	if (path === "") return [path];
	const segments = path.startsWith(".") ? [""] : [];
	for (const match of path.matchAll(pathSegmentPattern)) {
		const numberSegment = match[1];
		const quote = match[2];
		const quotedSegment = match[3];
		segments.push(quote && quotedSegment !== void 0 ? quotedSegment.replace(escapedCharacterPattern, "$1") : numberSegment ?? match[0]);
	}
	return segments;
};
/**
* Removes the property at `path` from `object` and mutates the object in place.
*
* This follows Lodash `unset` semantics: missing paths succeed, path arrays and
* dot/bracket notation are supported. Array indexes are removed with `splice`
* so Anvil form values remain dense after an item is removed.
*/
const unset = (object, path) => {
	if (object === null || object === void 0) return true;
	const segments = typeof path === "string" && isObject$1(object) && Object.hasOwn(object, path) ? [path] : parseUnsetPath(path);
	if (segments.length === 0) return true;
	let parent = object;
	for (const segment of segments.slice(0, -1)) {
		if (!isObject$1(parent) || !Object.hasOwn(parent, segment)) return true;
		parent = parent[segment];
	}
	if (!isObject$1(parent)) return true;
	const key = segments[segments.length - 1];
	if (!Object.hasOwn(parent, key)) return true;
	if (Array.isArray(parent)) {
		const index = Number(key);
		if (Number.isInteger(index) && index >= 0 && index < parent.length && index.toString() === key) {
			parent.splice(index, 1);
			return true;
		}
	}
	return Reflect.deleteProperty(parent, key);
};
//#endregion
//#region src/core/anvil-form.ts
/**
* Copies caller field config into Anvil-owned state so later caller mutations do
* not silently change registration behavior.
*/
const cloneFieldConfig = (config) => ({
	defaultValue: config.defaultValue,
	validation: config.validation,
	disabled: config.disabled ?? false,
	readonly: config.readonly ?? false,
	required: config.required ?? false,
	validateOn: config.validateOn ? [...config.validateOn] : void 0
});
/**
* Narrows persisted cache entries to the store's `{ _v, _d }` wrapper shape
* before hydration writes them into l3.
*/
const isAnvilStoreValue = (value) => {
	if (typeof value !== "object" || value === null) return false;
	return "_d" in value && "_v" in value && typeof value._d === "number";
};
/**
* Validates a persisted flat layer map before applying it, preventing malformed
* cache data from corrupting the current store.
*/
const isAnvilStoreRawValues = (values) => Object.values(values).every((value) => isAnvilStoreValue(value));
const groupItemCleanupLayers = [
	"l1",
	"l2",
	"l3",
	"l4"
];
/**
* Clamps user-supplied insertion indexes so group inserts stay predictable even
* when callers pass negative or out-of-range positions.
*/
const normalizeGroupIndex = (index, length) => {
	if (index < 0) return 0;
	if (index > length) return length;
	return index;
};
var AnvilForm = class {
	store;
	get events() {
		return this.store.events;
	}
	fieldRegistry;
	stateSubscription;
	formId;
	validateOn;
	asyncValidator;
	changeDebounceMs;
	persister = void 0;
	hydration;
	persistenceOperationQueue = Promise.resolve();
	fieldConfigs = {};
	groupState = {};
	registeringGroupPaths = /* @__PURE__ */ new Set();
	activeField;
	asyncValidationRunId = 0;
	submitRunId = 0;
	explicitValidation;
	asyncErrors = /* @__PURE__ */ new Map();
	asyncWarnings = /* @__PURE__ */ new Map();
	/**
	* Creates an Anvil form wrapper around a new AnvilStore.
	*
	* Construct this when an application needs form and field event behavior on
	* top of the lower-level value store.
	*
	* @example
	* const form = new AnvilForm({
	*   defaultValues: { email: "" },
	*   validateOn: ["blur", "submit"],
	* });
	*/
	constructor(config) {
		debugAnvil("constructor", () => ({
			formId: config.formId,
			hasDefaultValues: config.defaultValues !== void 0,
			hasInitialValues: config.initialValues !== void 0,
			hasLocalValues: config.localValues !== void 0,
			hasPersistenceService: config.persistenceService !== void 0,
			validateOn: config.validateOn
		}));
		this.store = new AnvilStore({
			events: {
				...config.events,
				entityId: config.formId
			},
			defaultValues: config.defaultValues,
			initialValues: config.initialValues,
			initialValuesUpdatedAt: config.initialValuesUpdatedAt,
			localValues: config.localValues
		});
		this.persister = config.persistenceService;
		this.fieldRegistry = config.fieldRegistry;
		this.stateSubscription = new AnvilFormStateSubscriptionController(this);
		this.formId = config.formId;
		this.asyncValidator = config.asyncValidator;
		this.changeDebounceMs = config.changeDebounceMs ?? 150;
		this.validateOn = config.validateOn ? [...config.validateOn] : ["submit"];
		this._setupPersistence(config.persistenceService, config.persistenceDebounceMs ?? this.changeDebounceMs);
	}
	getState(path, options = {}) {
		debugAnvil("getState", () => ({
			path,
			includeChildren: options.includeChildren
		}));
		const storeState = this.store.state;
		if (path !== void 0) return Object.freeze(this._createFieldState(path, storeState, options));
		const fieldPaths = new Set([...Object.keys(this.fieldConfigs), ...Object.keys(storeState.fieldState)]);
		const fields = {};
		for (const path of fieldPaths) fields[path] = Object.freeze(this._createFieldState(path, storeState));
		return Object.freeze({
			formId: this.formId,
			values: Object.freeze(this.store.getFormValue()),
			fields: Object.freeze(fields),
			errors: Object.freeze(this._createIssueMap(storeState.errors)),
			activeField: this.activeField,
			hasChanges: storeState.hasChanges,
			isDirty: storeState.isDirty,
			isTouched: storeState.isTouched,
			isValid: storeState.isValid,
			submitCount: storeState.submitCount,
			isSubmitted: storeState.isSubmitted,
			isValidating: storeState.isValidating,
			isSubmitting: storeState.isSubmitting,
			isSubmitSuccessful: storeState.isSubmitSuccessful
		});
	}
	/**
	* Registers field-level configuration and creates store metadata before a
	* field is interacted with.
	*
	* Use this from UI bindings when mounting an input. The returned function
	* unregisters the field and is intended for unmount cleanup.
	*
	* @example
	* const unregister = form.registerField("email", {
	*   required: true,
	*   validateOn: ["blur"],
	* });
	*
	* unregister();
	*/
	registerField(path, config = {}, options = {}) {
		return this.events.run("form.registerField", "system", () => {
			const currentConfig = this.fieldConfigs[path];
			const mergedConfig = cloneFieldConfig({
				...currentConfig,
				...config
			});
			const configChanged = shallowDiff(currentConfig, mergedConfig);
			debugAnvil("registerField", () => ({
				path,
				configChanged,
				defaultValueChanged: shallowDiff(currentConfig?.defaultValue, mergedConfig.defaultValue),
				hasValidation: mergedConfig.validation !== void 0,
				ignoreWhenUnregistered: options.ignoreWhenUnregistered ?? false,
				skipUnregister: options.skipUnregister
			}));
			if (configChanged) {
				this.events.record("configuration", path, this._getEventFieldConfig(currentConfig), this._getEventFieldConfig(mergedConfig));
				this.fieldConfigs[path] = mergedConfig;
			}
			/**
			* only reset default value if the default value has changed (allow undefined change)
			* null > undefined => change
			*/
			if (configChanged && mergedConfig.defaultValue !== void 0 && shallowDiff(currentConfig?.defaultValue, mergedConfig.defaultValue)) this.store.setLayerValue(path, mergedConfig.defaultValue, "l1");
			const unregisterStoreField = this.store.register(path, mergedConfig.validation, { ignoreWhenUnregistered: options.ignoreWhenUnregistered ?? false });
			return (unregisterOptions = {}) => {
				return this.events.run("form.unregisterField", "system", () => {
					debugAnvil("registerField.unregister", () => ({
						path,
						options: unregisterOptions,
						skipUnregister: options.skipUnregister
					}));
					if (options.skipUnregister) return;
					this._unregisterFieldConfig(path, unregisterOptions.removeState ?? false);
					unregisterStoreField(unregisterOptions);
				});
			};
		});
	}
	/**
	* Removes wrapper-only field metadata and publishes a new state snapshot.
	*
	* Use this when a dynamic field leaves the form so wrapper state like active
	* field tracking is cleared.
	*
	* @example
	* form.unregisterField("lineItems.0.name");
	*/
	unregisterField(path, options = {}) {
		return this.events.run("form.unregisterField", "system", () => {
			debugAnvil("unregisterField", () => ({
				path,
				options
			}));
			this._unregisterFieldConfig(path, options.removeState ?? false);
			this.store.unregister(path, options);
		});
	}
	/**
	* Unregisters the exact subform branch for a logical form path.
	*
	* Paths already below the reserved `__forms` root are ignored. Relative paths
	* are resolved below that root and options are forwarded unchanged.
	*/
	unregisterSubForms(path, options = {}) {
		return this.events.run("form.unregisterSubForms", "system", () => {
			if (isPathPrefixMatch(path, "__forms")) {
				debugAnvil("unregisterSubForms.skipped", () => ({
					path,
					options
				}));
				return;
			}
			const subFormsPath = joinPath(anvilSubFormsRootPath, path);
			debugAnvil("unregisterSubForms", () => ({
				path,
				subFormsPath,
				options
			}));
			this.unregisterField(subFormsPath, options);
		});
	}
	/**
	* Registers a repeatable field group and initializes its stable item keys.
	*
	* UI adapters call this when a group hook mounts. Existing array values are
	* read from the store, normalized to keyed entries, and padded to `min` when
	* configured.
	*/
	registerGroup(options) {
		return this.events.run("form.registerGroup", "system", () => {
			if (this.registeringGroupPaths.has(options.name)) {
				debugAnvil("registerGroup.reentrant", () => ({ name: options.name }));
				return this.getGroup(options.name);
			}
			this.registeringGroupPaths.add(options.name);
			try {
				return this._registerGroup(options);
			} finally {
				this.registeringGroupPaths.delete(options.name);
			}
		});
	}
	/**
	* Applies one group registration transaction. Writes can synchronously notify
	* subscribers, so the public entrypoint prevents this method from re-entering
	* for the same path before the current key update completes.
	*/
	_registerGroup(options) {
		const disabled = options.disabled ?? false;
		const min = disabled ? void 0 : options.min;
		const previousGroup = this.groupState[options.name];
		const storeKeys = this._getGroupKeysFromStore(options.name);
		const compactedKeys = this._withRemovedEmptyGroupKeys(options.name, storeKeys, min);
		const resizedKeys = this._withDecreasedMinimumGroupKeys(options.name, compactedKeys, previousGroup?.min, min);
		const keys = this._withMinimumGroupKeys(resizedKeys, min);
		debugAnvil("registerGroup", () => ({
			name: options.name,
			min: options.min,
			effectiveMin: min,
			max: options.max,
			disabled,
			previousMin: previousGroup?.min,
			storeKeys,
			resizedKeys,
			keys,
			hasDefaults: options.defaults !== void 0
		}));
		const nextGroup = {
			name: options.name,
			min: options.min,
			max: options.max,
			disabled,
			defaults: disabled ? void 0 : options.defaults,
			keys
		};
		this.events.record("group", options.name, this._getEventGroup(previousGroup), this._getEventGroup(nextGroup));
		this.groupState[options.name] = nextGroup;
		const targetLayer = this._hasGroupNonDefaultKeyMarker(options.name) ? "l4" : "l1";
		if (keys.length > storeKeys.length) this._writeGroupItems(options.name, keys, {}, targetLayer, { updateFieldState: false });
		else if (shallowDiff(keys, storeKeys)) this._writeGroupKeys(options.name, keys, targetLayer, { updateFieldState: false });
		this._writeGroupDefaultItemsFromGroup(this.groupState[options.name]);
		return this.getGroup(options.name);
	}
	/**
	* Returns the public render model for a group.
	*
	* The key is stable for React rendering; the path is indexed so controlled
	* fields can use normal form paths like `items.0.label`.
	*/
	getGroup(name) {
		return this._ensureGroup(name).keys.map((key, index) => ({
			key,
			index,
			path: `${name}.${key}`
		}));
	}
	/**
	* Adds one item to the end of a group when `max` allows it.
	*/
	appendGroup(name, initialValue) {
		return this.events.run("form.appendGroup", "user", () => {
			const group = this._ensureGroup(name);
			debugAnvil("appendGroup", () => ({
				name,
				disabled: group.disabled,
				keyCount: group.keys.length,
				max: group.max,
				hasInitialValue: initialValue !== void 0
			}));
			if (group.disabled) return this.getGroup(name);
			if (!this._canGrowGroup(group, 1)) return this.getGroup(name);
			const key = this._createGroupKey(group.keys);
			group.keys = [...group.keys, key];
			this._writeGroupItems(name, group.keys, this._createInitialGroupItems(key, initialValue));
			this._writeGroupDefaultItemsFromGroup(group);
			this._queueGroupValidation(name);
			return this.getGroup(name);
		});
	}
	/**
	* Adds one item to the start of a group when `max` allows it.
	*/
	prependGroup(name, initialValue) {
		return this.events.run("form.prependGroup", "user", () => {
			return this.insertGroup(name, 0, initialValue);
		});
	}
	/**
	* Inserts one item at the requested index, clamping out-of-range indexes to
	* the closest valid insertion point.
	*/
	insertGroup(name, index, initialValue) {
		return this.events.run("form.insertGroup", "user", () => {
			const group = this._ensureGroup(name);
			debugAnvil("insertGroup", () => ({
				name,
				index,
				disabled: group.disabled,
				keyCount: group.keys.length,
				max: group.max,
				hasInitialValue: initialValue !== void 0
			}));
			if (group.disabled) return this.getGroup(name);
			if (!this._canGrowGroup(group, 1)) return this.getGroup(name);
			const safeIndex = normalizeGroupIndex(index, group.keys.length);
			const key = this._createGroupKey(group.keys);
			group.keys = [
				...group.keys.slice(0, safeIndex),
				key,
				...group.keys.slice(safeIndex)
			];
			this._writeGroupItems(name, group.keys, this._createInitialGroupItems(key, initialValue));
			this._writeGroupDefaultItemsFromGroup(group);
			this._queueGroupValidation(name);
			return this.getGroup(name);
		});
	}
	/**
	* Removes one item by index or stable key while respecting the group `min`.
	*/
	removeGroup(name, indexOrKey) {
		return this.events.run("form.removeGroup", "user", () => {
			const group = this._ensureGroup(name);
			const min = group.min ?? 0;
			debugAnvil("removeGroup", () => ({
				name,
				indexOrKey,
				disabled: group.disabled,
				keyCount: group.keys.length,
				min
			}));
			if (group.disabled) return this.getGroup(name);
			if (group.keys.length <= min) return this.getGroup(name);
			const index = typeof indexOrKey === "number" ? indexOrKey : group.keys.findIndex((key) => key === indexOrKey);
			if (index < 0 || index >= group.keys.length) return this.getGroup(name);
			group.keys = group.keys.filter((_key, keyIndex) => keyIndex !== index);
			this._writeGroupItems(name, group.keys);
			this._queueGroupValidation(name);
			return this.getGroup(name);
		});
	}
	/**
	* Moves one existing item to another rendered index without changing its key.
	*/
	moveGroup(name, from, to) {
		return this.events.run("form.moveGroup", "user", () => {
			const group = this._ensureGroup(name);
			debugAnvil("moveGroup", () => ({
				name,
				from,
				to,
				disabled: group.disabled,
				keyCount: group.keys.length
			}));
			if (group.disabled) return this.getGroup(name);
			if (from < 0 || from >= group.keys.length || to < 0 || to >= group.keys.length || from === to) return this.getGroup(name);
			const movedKey = group.keys[from];
			if (!movedKey) return this.getGroup(name);
			const nextKeys = group.keys.filter((_key, index) => index !== from);
			nextKeys.splice(to, 0, movedKey);
			group.keys = nextKeys;
			this._writeGroupItems(name, group.keys);
			this._queueGroupValidation(name);
			return this.getGroup(name);
		});
	}
	/**
	* Marks a field as active.
	*
	* UI event adapters should call this from focus handlers. Validation is not
	* performed here.
	*
	* @example
	* input.addEventListener("focus", () => form.focusField("email"));
	*/
	focusField(path) {
		return this.events.run("form.focusField", "user", () => {
			debugAnvil("focusField", () => ({ path }));
			this._ensureField(path);
			this._setActiveField(path);
		});
	}
	/**
	* Marks a field as touched, clears active field when needed, and optionally
	* validates on blur.
	*
	* UI event adapters should call this from blur handlers. Validation runs when
	* `validateOn` includes `"blur"`.
	*
	* @example
	* input.addEventListener("blur", () => {
	*   void form.blurField("email");
	* });
	*/
	blurField(path) {
		return this.events.run("form.blurField", "user", () => {
			debugAnvil("blurField", () => ({
				path,
				shouldValidate: this._shouldValidate(path, "blur")
			}));
			this._ensureField(path);
			if (this.activeField === path) this._setActiveField(void 0);
			this.setFieldTouched(path, true);
			if (this._shouldValidate(path, "blur")) {
				this._queueFieldValidation(path);
				this._queueAsyncValidation();
			}
		});
	}
	/**
	* Writes a user-originated value change and optionally validates on change.
	*
	* UI event adapters should call this from change/input handlers. Validation
	* runs when `validateOn` includes `"change"`.
	*
	* @example
	* form.changeField("email", "person@example.com");
	*/
	changeField(path, value) {
		return this.events.run("form.changeField", "user", () => {
			debugAnvil("changeField", () => ({
				path,
				value,
				shouldValidate: this._shouldValidate(path, "change")
			}));
			this.setFieldValue(path, value, {
				source: "user",
				layer: "l4"
			});
			if (this._shouldValidate(path, "change")) {
				this._queueFieldValidation(path);
				this._queueAsyncValidation();
			}
		});
	}
	/**
	* Writes a value to a specific store layer without requiring a DOM event.
	*
	* Use this for programmatic updates, hydration, or framework adapters that
	* already normalized input values before touching AnvilForm.
	*
	* @example
	* form.setFieldValue("email", "person@example.com", {
	*   source: "system",
	*   layer: "l4",
	* });
	*/
	setFieldValue(path, value, options = {}) {
		return this.events.run("form.setFieldValue", options.source ?? "system", () => {
			const source = options.source ?? "system";
			const layer = options.layer ?? "l4";
			debugAnvil("setFieldValue", () => ({
				path,
				value,
				source,
				layer
			}));
			this._ensureField(path);
			this.store.setValue(path, value, source, layer);
		});
	}
	/**
	* Writes a tombstone for a field so lower layers cannot reintroduce a deleted
	* default, initial, or cached value.
	*/
	deleteFieldValue(path, options = {}) {
		return this.events.run("form.deleteFieldValue", options.source ?? "system", () => {
			const source = options.source ?? "system";
			const layer = options.layer ?? "l4";
			debugAnvil("deleteFieldValue", () => ({
				path,
				source,
				layer
			}));
			this._ensureField(path);
			this.store.deleteValue(path, source, layer);
		});
	}
	/**
	* Replaces one configured input layer with public form values.
	*
	* React adapters use this when `defaultValues`, `initialValues`, or
	* `localValues` change after the form instance has already been created.
	*/
	setValueLayer(layer, values = {}, options = {}) {
		return this.events.run("form.setValueLayer", "system", () => {
			debugAnvil("setValueLayer", () => ({
				layer,
				values,
				timestamp: options.timestamp
			}));
			this.store.setLayer(layer, this._createLayerFromInitialValues(values, options.timestamp));
			this.store.flush();
		});
	}
	/**
	* Sets touched state for a field.
	*
	* Use this from wrappers that need explicit touched control, or from custom
	* interaction models where blur is not the only way a field becomes touched.
	*
	* @example
	* form.setFieldTouched("email", true);
	*/
	setFieldTouched(path, touched) {
		return this.events.run("form.setFieldTouched", "system", () => {
			debugAnvil("setFieldTouched", () => ({
				path,
				touched
			}));
			this._ensureField(path);
			this.store.setFieldTouched(path, touched);
		});
	}
	/**
	* Sets or clears a manual field error.
	*
	* Use this for server-side validation results or external checks that should
	* be represented in Anvil's field error state.
	*
	* @example
	* form.setFieldError("email", "Email is already taken");
	* form.setFieldError("email");
	*/
	setFieldError(path, message) {
		return this.events.run("form.setFieldError", "system", () => {
			debugAnvil("setFieldError", () => ({
				path,
				message
			}));
			this._ensureField(path);
			this.store.setFieldError(path, message);
		});
	}
	/**
	* Sets or clears a manual field warning.
	*
	* Use this for non-blocking server-side validation results or soft checks
	* that should be visible to UI without making the field invalid.
	*
	* @example
	* form.setFieldWarning("email", "This address is outside your organization");
	* form.setFieldWarning("email");
	*/
	setFieldWarning(path, message) {
		return this.events.run("form.setFieldWarning", "system", () => {
			debugAnvil("setFieldWarning", () => ({
				path,
				message
			}));
			this._ensureField(path);
			this.store.setFieldWarning(path, message);
		});
	}
	/**
	* Runs validation for one field immediately.
	*
	* Use this for explicit validation commands, tests, or submit workflows.
	*
	* @example
	* form.validateField("email");
	*/
	validateField(path) {
		return this.events.run("form.validateField", "system", () => {
			debugAnvil("validateField", () => ({
				path,
				ignored: this.store.isFieldIgnored(path),
				hasValidation: this.fieldConfigs[path]?.validation !== void 0
			}));
			if (this.store.isFieldIgnored(path)) return;
			this._ensureField(path);
			const wasValidating = this.store.state.isValidating;
			try {
				this.store.validate(path, this.fieldConfigs[path]?.validation);
			} finally {
				if (wasValidating) {
					this.store.state.isValidating = true;
					this.store.notifySubscribers();
				}
			}
		});
	}
	/**
	* Validates registered fields and awaits the whole-form validator, including
	* values whose controls are not mounted.
	*
	* Use this before custom submit flows or when a UI needs to validate all
	* fields at once.
	*
	* @example
	* const canContinue = await form.validateForm();
	*/
	async validateForm() {
		if (this.hydration) await this.hydration;
		return this.events.run("form.validateForm", "system", async () => {
			debugAnvil("validateForm", () => ({ fieldPaths: Object.keys(this.fieldConfigs) }));
			for (const path of Object.keys(this.fieldConfigs)) this.validateField(path);
			const run = {
				values: this.store.getFormValue(),
				submitRunId: this.submitRunId
			};
			this.explicitValidation = run;
			try {
				return await this._runAsyncValidation(this._createValidationHelpers()) && this.store.state.isValid && this.submitRunId === run.submitRunId && this.store.getFormValue() === run.values;
			} finally {
				if (this.explicitValidation === run) this.explicitValidation = void 0;
			}
		});
	}
	/**
	* Resets form values and interaction metadata.
	*
	* Use this when returning a form to a known state, such as after a successful
	* save or when replacing loaded initial values.
	*
	* @example
	* await form.reset({ email: "person@example.com" });
	*/
	async reset(values, options) {
		return this.events.run("form.reset", "system", () => {
			debugAnvil("reset", () => ({
				values,
				options,
				registeredFields: Object.keys(this.fieldConfigs)
			}));
			this.asyncValidationRunId += 1;
			this.submitRunId += 1;
			this.asyncErrors.clear();
			this.asyncWarnings.clear();
			const nextState = createDefaultAnvilState();
			for (const [path, config] of Object.entries(this.fieldConfigs)) {
				nextState.fieldState[path] = createFieldState(config.validation);
				nextState.errors[path] = [];
				nextState.warnings[path] = [];
			}
			this._setActiveField(void 0);
			this.store.replaceState(nextState);
			if (options?.retainL2 === false) this.store.setLayer("l2", this._createLayerFromInitialValues({}));
			if (options?.retainL3 === false) {
				this._queuePersistenceDelete(this.persister);
				this.store.setLayer("l3", this._createLayerFromInitialValues({}));
			}
			this.store.setLayer("l4", this._createLayerFromInitialValues(values ?? {}));
			this.store.flush();
			return this.persistenceOperationQueue;
		});
	}
	/**
	* Runs submit-triggered validation, then delegates to the underlying store
	* submit flow.
	*
	* Use this as the single submit entrypoint for UI adapters so callbacks
	* receive immutable rolled-up values and immutable form state.
	*
	* @example
	* await form.submit(async (values, state) => {
	*   await save(values, state);
	* });
	*/
	async submit(callback, options = {}) {
		const submitRunId = ++this.submitRunId;
		if (this.hydration) await this.hydration;
		if (this.submitRunId !== submitRunId) return;
		debugAnvil("submit.start", () => ({
			options,
			validateOn: this.validateOn,
			isValidBeforeSubmit: this.store.state.isValid
		}));
		const context = this.events.run("form.submit", "user", () => this.events.captureContext());
		const submitValues = structuredClone(this.store.getFormValue());
		Object.freeze(submitValues);
		const submitPayload = this._createSubmitPayloadFromSnapshot(submitValues);
		const submitHelpers = this.events.withContext(context, () => this._createSubmitHelpers());
		if (!options.skipValidation) {
			const asyncValidationResult = await this.events.withContext(context, () => this._runAsyncValidation(this._createValidationHelpers(), submitValues));
			debugAnvil("submit.asyncValidationResult", () => ({
				asyncValidationResult,
				isValid: this.store.state.isValid
			}));
			if (this.submitRunId !== submitRunId || !asyncValidationResult || !this.store.state.isValid) {
				debugAnvil("submit.blocked.asyncValidation", () => ({
					asyncValidationResult,
					isValid: this.store.state.isValid
				}));
				await this._focusFirstErroredField();
				return;
			}
		}
		if (!options.skipValidation && this.validateOn.includes("submit")) this.events.withContext(context, () => this._queueFormValidation());
		if (!options.skipValidation && !this.store.state.isValid) {
			debugAnvil("submit.blocked.validation", () => ({
				isValid: this.store.state.isValid,
				errors: this.store.state.errors
			}));
			await this._focusFirstErroredField();
			return;
		}
		const result = await this.events.withContext(context, () => this.store.submit(async () => callback(submitPayload, this._createSubmitStateSnapshot(submitValues), submitHelpers), options));
		if (!options.skipValidation && !this.store.state.isValid) await this._focusFirstErroredField();
		debugAnvil("submit.end", () => ({
			isValid: this.store.state.isValid,
			isSubmitSuccessful: this.store.state.isSubmitSuccessful
		}));
		return result;
	}
	/**
	* Creates normalized field handlers for UI libraries.
	*
	* React, Svelte, and other wrappers should expose these handlers so UI
	* components do not call store methods directly.
	*
	* @example
	* const handlers = form.getFieldHandlers("email");
	* input.onfocus = handlers.onFocus;
	* input.onblur = () => void handlers.onBlur();
	*/
	getFieldHandlers(path) {
		return {
			onFocus: () => {
				this.focusField(path);
			},
			onBlur: () => {
				this.blurField(path);
			},
			onChange: (value) => {
				this.changeField(path, value);
			}
		};
	}
	/**
	* Builds the public field-state snapshot by merging store interaction state,
	* ignored status, and optionally rolled-up descendant issues.
	*/
	_createFieldState(path, storeState, options = {}) {
		const fieldState = storeState.fieldState[path];
		const indexedPath = this.store.getIndexedPath(path);
		const errors = this._createIssues(path, indexedPath, storeState.errors, options);
		return {
			...this.store.isFieldIgnored(path) ? { ignored: true } : {},
			touched: fieldState?.touched ?? false,
			userTouched: fieldState?.userTouched ?? false,
			dirty: fieldState?.dirty ?? false,
			userDirty: fieldState?.userDirty ?? false,
			isValid: (fieldState?.isValid ?? true) && errors.length === 0,
			errors,
			warnings: this._createIssues(path, indexedPath, storeState.warnings, options)
		};
	}
	/**
	* Clones issues matching a field path, optionally including descendants for
	* group and section-level state subscriptions.
	*/
	_createIssues(path, indexedPath, issuesByPath, options = {}) {
		if (!options.includeChildren) {
			const issues = issuesByPath[path] ?? [];
			const indexedIssues = indexedPath === path ? [] : issuesByPath[indexedPath] ?? [];
			return [...issues, ...indexedIssues].map((issue) => this._cloneIssue(issue));
		}
		const issues = [];
		for (const [issuePath, pathIssues] of Object.entries(issuesByPath)) {
			if (pathIssues.length === 0) continue;
			if (!isPathPrefixMatch(this.store.getIndexedPath(issuePath), indexedPath)) continue;
			issues.push(...pathIssues.map((issue) => this._cloneIssue(issue)));
		}
		return issues;
	}
	/**
	* Creates the whole-form issue map returned from `getState` without exposing
	* mutable issue objects from the store.
	*/
	_createIssueMap(issuesByPath) {
		const nextIssuesByPath = {};
		for (const [path, issues] of Object.entries(issuesByPath)) nextIssuesByPath[path] = issues.map((issue) => this._cloneIssue(issue));
		return nextIssuesByPath;
	}
	/**
	* Copies one issue and its metadata so external consumers cannot mutate
	* internal validation state through a state snapshot.
	*/
	_cloneIssue(issue) {
		return {
			path: issue.path,
			code: issue.code,
			message: issue.message,
			source: issue.source,
			metadata: { ...issue.metadata }
		};
	}
	/**
	* Lazily creates group state for callers that mutate a group before a hook has
	* registered it explicitly.
	*/
	_ensureGroup(name) {
		const existingGroup = this.groupState[name];
		if (existingGroup) return existingGroup;
		debugAnvil("ensureGroup.create", () => ({ name }));
		this.registerGroup({ name });
		const group = this.groupState[name];
		if (!group) throw new Error(`Unable to create Anvil group state for ${name}.`);
		return group;
	}
	/**
	* Reads the current durable key order for a group from stored array items.
	*/
	_getGroupKeysFromStore(name) {
		const keys = [];
		for (const item of this._getGroupItems(name)) {
			const key = item[arrayItemKeyProperty];
			if (typeof key === "string" && key.length > 0) keys.push(key);
		}
		return keys;
	}
	/**
	* Reads retained group drafts so structural edits preserve hidden answers.
	* Missing item keys are generated with the store's array normalization utility.
	*/
	_getGroupItems(name) {
		const value = this.store.getDraftValue(name);
		if (!Array.isArray(value)) return [];
		const keys = createArrayKeys(value, arrayItemKeyProperty);
		return value.map((item, index) => {
			const key = keys[index] ?? `k${index}`;
			const nextItem = { [arrayItemKeyProperty]: key };
			if (!isArrayObject(item)) return nextItem;
			for (const [childPath, childValue] of Object.entries(item)) {
				if (childPath === "__key") continue;
				nextItem[childPath] = childValue;
			}
			return nextItem;
		});
	}
	/**
	* Rewrites the group array in the store using the requested key order.
	*
	* Existing item values are preserved by item key; new keys become empty object
	* items and are later populated by controlled field writes.
	*/
	_writeGroupItems(name, keys, initialItems = {}, layer = "l4", options = {}) {
		const currentItems = this._getGroupItems(name);
		const currentItemsByKey = {};
		for (const item of currentItems) {
			const key = item[arrayItemKeyProperty];
			if (typeof key === "string" && key.length > 0) currentItemsByKey[key] = item;
		}
		const removedItemPaths = Object.keys(currentItemsByKey).filter((key) => !keys.includes(key)).map((key) => joinPath(name, key));
		const nextItems = keys.map((key) => {
			const currentItem = currentItemsByKey[key];
			if (currentItem) return currentItem;
			const initialItem = initialItems[key];
			if (initialItem) return {
				...initialItem,
				[arrayItemKeyProperty]: key
			};
			return { [arrayItemKeyProperty]: key };
		});
		debugAnvil("writeGroupItems", () => ({
			name,
			keys,
			layer,
			updateFieldState: options.updateFieldState ?? true,
			currentItems,
			initialItems,
			removedItemPaths,
			nextItems
		}));
		if (options.updateFieldState ?? true) this.store.setValue(name, nextItems, "system", layer);
		else this.store.setLayerValue(name, nextItems, layer);
		this.store.deleteLayerValues(removedItemPaths, groupItemCleanupLayers);
	}
	/**
	* Writes group instance defaults into l1 so newly-created entries inherit
	* baseline values without counting as user or cache changes.
	*/
	_writeGroupDefaultItems(name, keys, defaults) {
		const defaultItems = keys.map((key) => ({
			...defaults,
			[arrayItemKeyProperty]: key
		}));
		debugAnvil("writeGroupDefaultItems", () => ({
			name,
			keys,
			defaults,
			defaultItems
		}));
		this.store.setLayerValue(name, defaultItems, "l1");
	}
	/**
	* Applies configured defaults to every current key, skipping groups with no
	* defaults or no entries.
	*/
	_writeGroupDefaultItemsFromGroup(group) {
		if (!group.defaults || group.keys.length === 0) return;
		this._writeGroupDefaultItems(group.name, group.keys, group.defaults);
	}
	/**
	* Rewrites only a group's key list when item values do not need to be rebuilt,
	* and removes value branches for keys that disappeared.
	*/
	_writeGroupKeys(name, keys, layer = "l4", options = {}) {
		const groupKeysPath = joinPath(name, arrayKeysProperty);
		const removedItemPaths = this._getGroupKeysFromStore(name).filter((key) => !keys.includes(key)).map((key) => joinPath(name, key));
		debugAnvil("writeGroupKeys", () => ({
			name,
			groupKeysPath,
			keys,
			layer,
			removedItemPaths,
			updateFieldState: options.updateFieldState ?? true
		}));
		if (options.updateFieldState ?? true) this.store.setValue(groupKeysPath, keys, "system", layer);
		else this.store.setLayerValue(groupKeysPath, keys, layer);
		this.store.deleteLayerValues(removedItemPaths, groupItemCleanupLayers);
	}
	/**
	* Creates a key-addressed payload map for newly inserted group items.
	*
	* The generated key is kept authoritative so callers can seed values without
	* taking ownership of Anvil's internal array identity.
	*/
	_createInitialGroupItems(key, initialValue) {
		if (!initialValue) return {};
		return { [key]: initialValue };
	}
	/**
	* Pads group keys up to the configured minimum. This lets a group render
	* required empty rows before the user has entered any values.
	*/
	_withMinimumGroupKeys(keys, min) {
		if (min === void 0 || keys.length >= min) return keys;
		const nextKeys = [...keys];
		while (nextKeys.length < min) nextKeys.push(this._createGroupKey(nextKeys));
		debugAnvil("withMinimumGroupKeys", () => ({
			keys,
			min,
			nextKeys
		}));
		return nextKeys;
	}
	/**
	* Removes empty instance shells that exist in storage but have no layer data,
	* while preserving the configured minimum count.
	*/
	_withRemovedEmptyGroupKeys(name, keys, min) {
		const targetMin = min ?? 0;
		if (keys.length <= targetMin) return keys;
		let next = keys.map((key) => this._hasGroupItemAnyLayerData(name, key) || this._hasGroupItemLocalLayerEntry(name, key) ? key : null);
		while (next.length > targetMin) {
			const idx = next.findLastIndex((key) => key === null);
			if (idx === -1) break;
			next = next.toSpliced(idx, 1);
		}
		debugAnvil("withRemovedEmptyGroupKeys", () => ({
			name,
			keys,
			min,
			next
		}));
		return next.map((_k, idx) => _k ? _k : keys[idx]);
	}
	/**
	* Handles a lowered `min` by removing untouched generated entries from the end
	* of the group, preserving any entry with l2/l3/l4 data.
	*/
	_withDecreasedMinimumGroupKeys(name, keys, previousMin, nextMin) {
		const targetMin = nextMin ?? 0;
		if (previousMin === void 0 || previousMin <= targetMin || keys.length <= targetMin) return keys;
		const removableCount = Math.min(previousMin - targetMin, keys.length - targetMin);
		const nextKeys = [...keys];
		let removedCount = 0;
		for (let keyIndex = nextKeys.length - 1; keyIndex >= 0 && removedCount < removableCount; keyIndex -= 1) {
			const key = nextKeys[keyIndex];
			if (!key || this._hasGroupItemNonDefaultLayerChanges(name, key)) {
				debugAnvil("withDecreasedMinimumGroupKeys.skipped", () => ({
					name,
					keys,
					previousMin,
					nextMin,
					blockedKey: key
				}));
				continue;
			}
			nextKeys.splice(keyIndex, 1);
			removedCount += 1;
		}
		debugAnvil("withDecreasedMinimumGroupKeys", () => ({
			name,
			keys,
			previousMin,
			nextMin,
			nextKeys
		}));
		return nextKeys;
	}
	/**
	* Checks every layer for meaningful data under one group item key so cleanup
	* does not remove entries that came from defaults, initial data, cache, or UI.
	*/
	_hasGroupItemAnyLayerData(name, key) {
		const itemPath = joinPath(name, key);
		return this._hasGroupItemLayerData(this.store.getValue(itemPath, "l4")) || this._hasGroupItemLayerData(this.store.getValue(itemPath, "l3")) || this._hasGroupItemLayerData(this.store.getValue(itemPath, "l2")) || this._hasGroupItemLayerData(this.store.getValue(itemPath, "l1"));
	}
	/**
	* Detects l4 key-list membership even when the item branch is empty, because a
	* local key write still represents user-visible group structure.
	*/
	_hasGroupItemLocalLayerEntry(name, key) {
		const localKeys = this.store.getValue(joinPath(name, arrayKeysProperty), "l4");
		return isStringArray(localKeys) && localKeys.includes(key);
	}
	/**
	* Detects whether server, cache, or user data owns the group key list,
	* including an explicitly empty array. Minimum-only placeholders remain
	* defaults only while every non-default layer omits the group, preventing
	* them from outranking values that hydrate after registration.
	*/
	_hasGroupNonDefaultKeyMarker(name) {
		const groupKeysPath = joinPath(name, arrayKeysProperty);
		const initialKeys = this.store.getValue(groupKeysPath, "l2");
		const cachedKeys = this.store.getValue(groupKeysPath, "l3");
		const localKeys = this.store.getValue(groupKeysPath, "l4");
		return isStringArray(initialKeys) || isStringArray(cachedKeys) || isStringArray(localKeys);
	}
	/**
	* Checks whether an item has persisted, initial, or user-entered data that
	* should block minimum-decrease cleanup.
	*/
	_hasGroupItemNonDefaultLayerChanges(name, key) {
		const itemPath = joinPath(name, key);
		return this._hasGroupItemLayerData(this.store.getValue(itemPath, "l2")) || this._hasGroupItemLayerData(this.store.getValue(itemPath, "l3")) || this._hasGroupItemLayerData(this.store.getValue(itemPath, "l4"));
	}
	/**
	* Treats metadata and Anvil item identity as non-user data while detecting
	* whether a group item contains meaningful field values.
	*/
	_hasGroupItemLayerData(value) {
		if (value === void 0) return false;
		if (!isObject$1(value)) return true;
		return Object.entries(value).some(([path, childValue]) => {
			if (path === "_metadata" || path === "__key") return false;
			return !isObject$1(childValue) || Object.keys(childValue).length > 0;
		});
	}
	/**
	* Allocates the next compact monotonic key for this group scope.
	*/
	_createGroupKey(keys) {
		const items = keys.map((key) => ({ [arrayItemKeyProperty]: key }));
		items.push({});
		const nextKeys = createArrayKeys(items, arrayItemKeyProperty);
		return nextKeys[nextKeys.length - 1] ?? `k${keys.length}`;
	}
	/**
	* Checks the configured maximum before mutating group item order.
	*/
	_canGrowGroup(group, amount) {
		return group.max === void 0 || group.keys.length + amount <= group.max;
	}
	/**
	* Ensures wrapper and store field metadata exist before field events mutate
	* touched, dirty, error, or active-field state.
	*/
	_ensureField(path) {
		if (this.fieldConfigs[path]) return;
		debugAnvil("ensureField.register", () => ({ path }));
		this.registerField(path);
	}
	/**
	* Removes wrapper registration state and clears active focus if the
	* unregistered field was active. Subtree cleanup also removes descendants.
	*/
	_unregisterFieldConfig(path, includeDescendants = false) {
		debugAnvil("unregisterFieldConfig", () => ({
			path,
			includeDescendants,
			wasActive: this.activeField === path || includeDescendants && this.activeField !== void 0 && isPathPrefixMatch(this.activeField, path)
		}));
		for (const fieldPath of Object.keys(this.fieldConfigs)) {
			if (fieldPath !== path && (!includeDescendants || !isPathPrefixMatch(fieldPath, path))) continue;
			this.events.record("configuration", fieldPath, this._getEventFieldConfig(this.fieldConfigs[fieldPath]), void 0);
			delete this.fieldConfigs[fieldPath];
		}
		if (this.activeField === path || includeDescendants && this.activeField !== void 0 && isPathPrefixMatch(this.activeField, path)) this._setActiveField(void 0);
	}
	_getEventGroup(group) {
		if (!group) return;
		const { defaults: _defaults, ...data } = group;
		return data;
	}
	_getEventFieldConfig(config) {
		if (!config) return;
		const { validation, defaultValue: _defaultValue, ...data } = config;
		return {
			...data,
			hasValidation: validation !== void 0
		};
	}
	_setActiveField(path) {
		this.events.record("focus", "", this.activeField, path);
		this.activeField = path;
	}
	/**
	* Resolves field-level validation triggers first, falling back to the form
	* default trigger list when a field did not override it.
	*/
	_shouldValidate(path, event) {
		const fieldValidateOn = this.fieldConfigs[path]?.validateOn;
		if (fieldValidateOn) return fieldValidateOn.includes(event);
		return this.validateOn.includes(event);
	}
	/**
	* Centralizes sync field validation scheduling so event handlers and submit can
	* share the same validation path.
	*/
	_queueFieldValidation(path) {
		debugAnvil("queueFieldValidation", () => ({ path }));
		this.validateField(path);
	}
	_queueGroupValidation(path) {
		if (this._shouldValidate(path, "change")) {
			this._queueFieldValidation(path);
			this._queueAsyncValidation();
		}
	}
	/**
	* Queues validation for every wrapper-registered field during submit.
	*/
	_queueFormValidation() {
		debugAnvil("queueFormValidation", () => ({ fieldPaths: Object.keys(this.fieldConfigs) }));
		for (const path of Object.keys(this.fieldConfigs)) this._queueFieldValidation(path);
	}
	/**
	* Starts async form validation without forcing event handlers to await it.
	*/
	_queueAsyncValidation() {
		debugAnvil("queueAsyncValidation");
		const validation = this.explicitValidation;
		if (validation && validation.submitRunId === this.submitRunId && validation.values === this.store.getFormValue()) return;
		this._runAsyncValidation(this._createValidationHelpers()).catch(() => void 0);
	}
	/**
	* Runs the configured async validator with run-id protection so stale async
	* completions cannot clear a newer validation run's loading state.
	*/
	async _runAsyncValidation(helpers, submitValues) {
		if (!this.asyncValidator) {
			debugAnvil("runAsyncValidation.skipped");
			return true;
		}
		const runId = this.asyncValidationRunId + 1;
		this.asyncValidationRunId = runId;
		if (this.hydration) {
			await this.hydration;
			if (this.asyncValidationRunId !== runId) return false;
		}
		const nextErrors = /* @__PURE__ */ new Map();
		const nextWarnings = /* @__PURE__ */ new Map();
		const scopedHelpers = {
			setFieldError: (path, message) => {
				nextErrors.set(path, message);
			},
			setFieldWarning: (path, message) => {
				nextWarnings.set(path, message);
			}
		};
		const context = this.events.run("form.validateAsync", "system", () => {
			this.store.state.isValidating = true;
			this.store.notifySubscribers();
			return this.events.captureContext();
		});
		debugAnvil("runAsyncValidation.start", () => ({ runId }));
		try {
			const validationResult = await this.asyncValidator(submitValues ?? Object.freeze(this.store.getFormValue()), scopedHelpers);
			if (this.asyncValidationRunId === runId) this.events.withContext(context, () => this.store.batch(() => {
				this._replaceAsyncIssues(this.asyncErrors, nextErrors, this.store.state.errors, helpers.setFieldError);
				this._replaceAsyncIssues(this.asyncWarnings, nextWarnings, this.store.state.warnings, helpers.setFieldWarning);
			}));
			debugAnvil("runAsyncValidation.end", () => ({
				runId,
				validationResult
			}));
			if (submitValues) return validationResult !== false && ![...nextErrors.values()].some((message) => Boolean(message));
			return this.asyncValidationRunId === runId && validationResult !== false;
		} finally {
			if (this.asyncValidationRunId === runId) this.events.withContext(context, () => this.events.run("form.validateAsync.complete", "system", () => {
				this.store.state.isValidating = false;
				this.store.flush();
				this.store.notifySubscribers();
			}));
		}
	}
	/** Keep displayed issues stable while lookups run; publish the next result once. */
	_replaceAsyncIssues(previous, next, current, setIssue) {
		for (const [path, message] of previous) if (!next.has(path) && current[path]?.some((issue) => issue.code === "manual" && issue.message === message)) setIssue(path);
		previous.clear();
		for (const [path, message] of next) {
			setIssue(path, message);
			if (message !== void 0) previous.set(path, message);
		}
	}
	/**
	* Creates the restricted helper surface available during async validation.
	* Validators may report issues but cannot mutate or reset form values.
	*/
	_createValidationHelpers() {
		const context = this.events.captureContext();
		return {
			setFieldError: (path, message) => {
				this.events.withContext(context, () => {
					this._ensureField(path);
					this.store.setFieldError(path, message, { retainValidationErrors: true });
				});
			},
			setFieldWarning: (path, message) => {
				this.events.withContext(context, () => this.setFieldWarning(path, message));
			}
		};
	}
	/**
	* Creates submit helper callbacks that keep handler-side mutations routed
	* through the same form APIs as UI events.
	*/
	_createSubmitHelpers() {
		const context = this.events.captureContext();
		return {
			...this._createValidationHelpers(),
			setFieldValue: (path, value, shouldValidate) => {
				this.events.withContext(context, () => {
					this.setFieldValue(path, value, {
						source: "system",
						layer: "l4"
					});
					if (shouldValidate) {
						this._queueFieldValidation(path);
						this._queueAsyncValidation();
					}
				});
			},
			reset: (values, options) => {
				this.events.withContext(context, () => this.reset(values, options));
			}
		};
	}
	/**
	* Combines button-time values with the latest validation and submission
	* metadata so both submit callback arguments describe the same value snapshot.
	*/
	_createSubmitStateSnapshot(values) {
		return Object.freeze({
			...this.getState(),
			values
		});
	}
	/**
	* Separates a detached button-time snapshot into root form values and
	* normalized subform metadata while retaining ignored subform values.
	*/
	_createSubmitPayloadFromSnapshot(values) {
		const form = { ...values };
		const subForms = [];
		this._collectSubmitSubForms(values[anvilSubFormsRootPath], "", subForms);
		for (const subForm of subForms) {
			const resolvedPath = resolveFormValuePath(subForm.path, form);
			if (resolvedPath) unset(form, resolvedPath);
		}
		debugAnvil("createSubmitPayload", () => ({
			subFormCount: subForms.length,
			hasSubFormsRoot: values[anvilSubFormsRootPath] !== void 0,
			form,
			subForms
		}));
		return Object.freeze({
			form: Object.freeze(form),
			subForms: Object.freeze(subForms)
		});
	}
	/**
	* Recursively walks the reserved `__forms` tree and appends normalized subform
	* entries to the provided collection, omitting ignored branches.
	*/
	_collectSubmitSubForms(value, groupPath, subForms) {
		const subFormsPath = groupPath ? joinPath(anvilSubFormsRootPath, groupPath) : anvilSubFormsRootPath;
		if (this.store.isFieldIgnored(subFormsPath)) return;
		if (Array.isArray(value)) {
			value.forEach((item, index) => {
				if (!isObject$1(item)) return;
				const key = typeof item["__key"] === "string" ? item[arrayItemKeyProperty] : index.toString();
				subForms.push(Object.freeze({
					path: joinPath(anvilSubFormsRootPath, groupPath, key),
					values: this._createSubFormSubmitValues(item),
					metadata: this._createSubFormSubmitMetadata(item)
				}));
			});
			return;
		}
		if (!isObject$1(value)) return;
		for (const [key, childValue] of Object.entries(value)) this._collectSubmitSubForms(childValue, joinPath(groupPath, key), subForms);
	}
	/**
	* Builds the submit value object for one subform item, preferring explicit
	* `_values` while preserving legacy direct child values.
	*/
	_createSubFormSubmitValues(item) {
		const explicitValues = item._values;
		const values = isObject$1(explicitValues) ? structuredClone(explicitValues) : {};
		for (const [key, value] of Object.entries(item)) {
			if (key === "__key" || key === "_metadata" || key === "_values") continue;
			values[key] = structuredClone(value);
		}
		return Object.freeze(values);
	}
	/**
	* Extracts submit metadata for one subform item without mixing it into the
	* user value payload.
	*/
	_createSubFormSubmitMetadata(item) {
		const metadata = item._metadata;
		if (!isObject$1(metadata)) return Object.freeze({});
		return Object.freeze(structuredClone(metadata));
	}
	/**
	* Focuses the first field with a blocking error after validation fails, giving
	* UI consumers a consistent post-submit navigation target.
	*/
	async _focusFirstErroredField() {
		const firstErrorPath = Object.entries(this.getState().fields).find(([_path, fieldState]) => fieldState.errors.length > 0)?.[0];
		if (!firstErrorPath) return false;
		return this.fieldRegistry.focusField(firstErrorPath);
	}
	/**
	* Builds a store layer from public input values without sharing durable path
	* maps across layer replacement calls.
	*/
	_createLayerFromInitialValues(values, timestamp) {
		return createLayerFromValues(values, {}, {}, {
			timestamp,
			useDurablePaths: false
		});
	}
	/**
	* Wires persistence hydration and debounced writes so l3 cache follows form
	* changes while clearing itself when the form matches initial values.
	*/
	_setupPersistence(persister, debounceMs) {
		if (!persister) {
			debugAnvil("setupPersistence.skipped");
			return;
		}
		debugAnvil("setupPersistence", () => ({
			debounceMs,
			key: persister.key(this.formId)
		}));
		let previous = this.store.getPersistenceSnapshot();
		const initialEdits = structuredClone(this.store.layers.l4._l);
		this.hydration = this._hydratePersistence(persister).catch(() => void 0).then(() => {
			if (deepEqual(initialEdits, this.store.layers.l4._l)) previous = this.store.getPersistenceSnapshot();
		}).finally(() => {
			this.hydration = void 0;
		});
		/**
		* @todo this can be optimized so it only stored l3 and l4 changes not entire form value
		*/
		this.store.subscribe({ next: async () => {
			if (this.hydration) await this.hydration;
			const next = this.store.getPersistenceSnapshot();
			if (deepEqual(previous, next)) return;
			previous = next;
			const { values, hasChanges } = next;
			if (!hasChanges) {
				this._queuePersistenceDelete(persister);
				return;
			}
			this._queuePersistenceSet(persister, values);
		} }, { debounceMs });
	}
	/**
	* Loads persisted l3 cache only when it matches the raw store value shape, then
	* rolls it into the form layer without immediately writing it back.
	*/
	async _hydratePersistence(service) {
		const values = await service.get(service.key(this.formId));
		if (!values) {
			debugAnvil("hydratePersistence.empty", () => ({ key: service.key(this.formId) }));
			return;
		}
		if (!isAnvilStoreRawValues(values)) {
			console.warn("Anvil form cache was invalid", values);
			return;
		}
		debugAnvil("hydratePersistence.apply", () => ({
			key: service.key(this.formId),
			values
		}));
		this.store.setLayer("l3", {
			_d: Date.now(),
			_l: values
		}, { notifySubscribers: false });
		this.store.flush();
	}
	/**
	* Serializes persistence side effects so an older async set cannot complete
	* after a later cache clear and restore stale form values.
	*/
	_queuePersistenceOperation(operation) {
		this.persistenceOperationQueue = this.persistenceOperationQueue.then(operation).catch(() => void 0);
	}
	_queuePersistenceDelete(persister) {
		if (!persister) return;
		const key = persister.key(this.formId);
		debugAnvil("persistence.delete", () => ({ key }));
		this._queuePersistenceOperation(() => persister.delete(key));
	}
	_queuePersistenceSet(persister, values) {
		const key = persister.key(this.formId);
		debugAnvil("persistence.set", () => ({
			key,
			values
		}));
		this._queuePersistenceOperation(() => persister.set(key, values));
	}
};
/**
* Creates an AnvilForm instance without requiring callers to use `new`.
*
* Prefer this factory in framework integrations so future construction details
* can change without altering wrapper code.
*
* @example
* const form = createAnvilForm({
*   defaultValues: { email: "" },
* });
*/
const createAnvilForm = (config) => new AnvilForm(config);
//#endregion
export { encodeAnvilEventValue as A, anvilDebugStorageKey as C, AnvilFormStateSubscriptionController as D, arrayKeysProperty as E, AnvilEvents as O, resolveFormValuePath as S, arrayItemKeyProperty as T, isPathRelated as _, AnvilStore as a, parsePath as b, anvilEmptyObjectValue as c, isAnvilStoreSentinel as d, getDurablePath as f, isPathPrefixMatch as g, getPrefixValues as h, shallowDiff as i, deepEqual as j, decodeAnvilEventValue as k, isAnvilDeletedValue as l, getPathWithArrayIndexes as m, createAnvilForm as n, createDefaultAnvilState as o, getOrCreateDurablePath as p, unset as r, anvilDeletedValue as s, AnvilForm as t, isAnvilEmptyObjectValue as u, joinPath as v, anvilSubFormsRootPath as w, replaceValueAtPath as x, numericPathSegmentPattern as y };

//# sourceMappingURL=anvil-form-DXTQEj__.js.map